/**
 * src/modules/orders-list/status-panel.ts — Faza 6.2.24 (2026-05-03) split.
 *
 * Lewy panel filtrów: Statusy / Płatność / Przesyłka / Źródło
 * (collapsible sections, persisted in localStorage `osp_collapsed`).
 * Dodatkowo: lazy refresh PS orderów z pustym adresem + `_refreshConnectedIntegrations`
 * (Allegro + PrestaShop + Erli + eBay accounts).
 *
 * Wycięte z `src/modules/orders-list.ts` (1651 LOC) — patrz
 * `.plans/audit-orders-list-split.md`.
 */

import type { IntegrationAccount, StatusMetaItem } from '../../types/dto.ts';
import { isOrdersGlobalSearchActive, syncOrdersSearchScopeUI } from '../orders-shared.ts';
import { zapamietajWidok } from '../plynne.ts';

var state: any = (typeof window !== 'undefined' && window.__sbState) || (function () {
  var _p = new Proxy({}, {
    get: function (_, k: any) { return window.__sbState ? (window.__sbState as any)[k] : undefined; },
    set: function (_, k: any, v: any) { if (window.__sbState) (window.__sbState as any)[k] = v; return true; },
    has: function (_, k: any) { return window.__sbState ? (k in (window.__sbState as any)) : false; },
  });
  return _p;
})();

/**
 * Rozwiniete grupy statusow — WYLACZNIE w pamieci, CELOWO bez localStorage.
 *
 * Decyzja usera (2026-08-10): grupa ma byc „zawsze zwinieta po wejsciu". Zmienna
 * modulowa daje dokladnie to: zyje do przeladowania strony, wiec kazde wejscie na
 * liste zaczyna od zwinietej grupy, a `_ospSetCollapsed` (localStorage) zostaje
 * nietkniete dla sekcji, ktore MAJA sie pamietac.
 *
 * DLACZEGO NIE „zwinieta przy kazdym rysowaniu": pasek przerysowuje sie wielokrotnie
 * w trakcie pracy — po zmianie filtra, po odswiezeniu danych, po synchronizacji.
 * Zwijanie przy kazdym renderze zamykaloby grupe zaraz po tym, jak user klika status
 * w jej srodku, czyli funkcja bylaby nie do uzycia.
 *
 * Klucz to id grupy. Brak wpisu = „user nie dotykal" i wtedy decyduje aktywny filtr.
 */
// 0239 (decyzja usera 2026-08-17): widoczność KATEGORII (statusów w grupie)
// i TAGÓW to DWA niezależne przełączniki na wierszu grupy, zapisywane NA
// GRUPIE w bazie (per firma — wspólne dla zespołu, przeżywają F5). Lokalny
// stan chevrona i jednorazowy podgląd tagów WYCIĘTE —
// jedna prawda zamiast trzech równoległych stanów.

/** 0245: ⊞/🏷 to od teraz OSOBISTA preferencja widoku (user_ui_prefs), nie edycja
 *  grupy — więc bramka orders:write ZNIKA: każdy zalogowany układa pasek pod siebie. */
function _mozeZmieniacWidokGrup(): boolean {
  return true;
}

/** 0245: efektywny widok grupy = NADPISANIE usera (klucz 'widok_grup') → domyślka
 *  z flagi grupy. Flagi na grupie (show_statuses/show_tags) zostają domyślką
 *  firmową ustawianą w edycji grupy — klik w pasku ich już NIE dotyka. */
function _widokGrupy(g: any, rodzaj: 'statuses' | 'tags' | 'production'): boolean {
  // 2026-08-25 (zgłoszenie usera: „odznaczyłem rozdzielanie wg tagów w grupie
  // DO DRUKU, a i tak wyświetla tagi"): USTAWIENIE GRUPY jest nadrzędne nad
  // osobistym widokiem. Grupa z wyłączonym rozdzielaniem nie ma się z czego
  // rozwijać — 🏷 na pasku steruje tylko tam, gdzie firma to rozdzielanie
  // włączyła. Wcześniej wygrywało nadpisanie usera, więc kto raz kliknął 🏷,
  // ten widział tagi mimo zmiany w ustawieniach grupy.
  if (rodzaj !== 'production') {
    var poleFirmy = rodzaj === 'statuses' ? 'show_statuses' : 'show_tags';
    if ((g as any)[poleFirmy] === false) return false;
  }
  try {
    var mapa: any = (window as any).__sbStateExt && (window as any).__sbStateExt._widokGrupUser;
    var wpis = mapa && mapa[String((g as any).id)];
    if (wpis && typeof wpis[rodzaj] === 'boolean') return wpis[rodzaj];
  } catch (_e) { /* brak nadpisania → domyślka */ }
  // PRODUKCJA v2 (odbiór usera 2026-08-19): wiersze produkcji DOMYŚLNIE
  // ROZWINIĘTE — to one są treścią grupy roboczej; 🔧 zwija per użytkownik.
  if (rodzaj === 'production') return true;
  var pole = rodzaj === 'statuses' ? 'show_statuses' : 'show_tags';
  return (g as any)[pole] !== false;
}

/** Dwa przyciski widoku grupy: ⊞ kategorie (nie dla grup ręcznych) + 🏷 tagi. */
function _przyciskiWidokuGrupy(g: any, idG: string, reczna: boolean): string {
  if (!_mozeZmieniacWidokGrup()) return '';
  var pokazKat = _widokGrupy(g, 'statuses');
  var pokazTagi = _widokGrupy(g, 'tags');
  var wspolny = 'cursor:pointer;line-height:1;font-size:11px;padding:1px 3px;border-radius:5px;';
  var html = '';
  // Odbiór usera 2026-08-19 #2: grupa RĘCZNA pokazuje WYŁĄCZNIE stany
  // produkcji — rozkład statusów zamówień („W realizacji") wykodowany,
  // więc ⊞ zostaje tylko przy grupach automatycznych.
  if (!reczna) {
    var tytulKat = pokazKat ? 'Ukryj kategorie (statusy) tej grupy' : 'Pokaż kategorie (statusy) tej grupy';
    html += '<span data-osp-widok="statuses" data-osp-widok-grupa="' + escapeAttr(idG) + '"' +
      ' role="button" tabindex="0"' +
      ' title="' + tytulKat + ' — Twój osobisty widok"' +
      ' style="' + wspolny + 'opacity:' + (pokazKat ? '1' : '.45') + '">⊞</span>';
  }
  // Grupa z wyłączonym rozdzielaniem: ikonka 🏷 zniknięta, bo klik i tak nic
  // by nie zmienił (decyduje ustawienie grupy). Włącza się je w Ustawieniach.
  // Celowo pole FIRMY, nie _widokGrupy: nadpisanie usera nie może chować
  // przełącznika, którym by je cofnął.
  if ((g as any).show_tags === false) return html; // firmowe-pole-celowo
  html += '<span data-osp-widok="tags" data-osp-widok-grupa="' + escapeAttr(idG) + '"' +
    ' role="button" tabindex="0"' +
    ' title="' + (pokazTagi ? 'Ukryj tagi tej grupy' : 'Pokaż tagi tej grupy') + ' — Twój osobisty widok"' +
    ' style="' + wspolny + 'opacity:' + (pokazTagi ? '1' : '.45') + '">🏷</span>';
  // PRODUKCJA v2: 🔧 rozwija stany produkcji ZAWARTOŚCI grupy roboczej
  // („Montuje · Paweł 2") — osobisty widok, domyślnie zwinięty.
  if (reczna) {
    var pokazProd = _widokGrupy(g, 'production');
    html += '<span data-osp-widok="production" data-osp-widok-grupa="' + escapeAttr(idG) + '"' +
      ' role="button" tabindex="0"' +
      ' title="' + (pokazProd ? 'Ukryj stany produkcji' : 'Pokaż stany produkcji (kto co robi)') + ' — Twój osobisty widok"' +
      ' style="' + wspolny + 'opacity:' + (pokazProd ? '1' : '.45') + '">🔧</span>';
  }
  return html;
}

function _kontenerTagowHtml(g: any, idG: string, wartoscG: string, dimsG: string, shipsG: string): string {
  if (!_widokGrupy(g, 'tags')) return '';
  return '<div class="osp-grupa-tagi" data-osp-tagi-grupa="' + escapeAttr(idG) + '"' +
    ' data-tagi-status="' + escapeAttr(wartoscG) + '"' +
    (dimsG ? ' data-tagi-pdims="' + escapeAttr(dimsG) + '"' : '') +
    (shipsG ? ' data-tagi-sdims="' + escapeAttr(shipsG) + '"' : '') +
    // 2026-08-21 (prośba usera): tagi jeden pod drugim, nie obok siebie — przy trzech
    // chipach w wierszu nazwy się urywały i nie dało się ich odczytać.
    ' style="padding:2px 10px 8px 24px;display:flex;flex-direction:column;align-items:stretch;gap:4px">' +
    '<span style="font-size:10px;color:var(--color-text-faint)">tagi…</span></div>';
}
// 2026-09-03 (decyzja usera): CZARNA LISTA to JEDNA grupa SYSTEMOWA na dole listy
// grup (pod statusami, grupami automatycznymi i roboczymi), a nie chip pod każdą
// grupą. Trzyma wszystkie zamówienia kupujących z żywym trafieniem czarnej listy,
// niezależnie od statusu, tagów i grup roboczych — te wypadają z innych grup
// (pierwszeństwo jak grupy roboczej nad automatyczną; „Wszystkie" zostaje pełne).
function _czarnaListaAktywna(): boolean {
  var _ot = (window as any).__sbOrderTags;
  return !!(_ot && _ot.czarnaListaFiltr && _ot.czarnaListaFiltr());
}
/** 2026-09-08 (prośba właściciela: „status/tag też w nowej karcie"): adres listy z filtrem paska —
 * `/orders?t=<typ>&v=<wartość>[&d=<płatność>][&s=<wysyłka>][&tg=<id tagów>]`. Nazwa grupy i chip tagu
 * są prawdziwymi linkami (PPM / środkowy przycisk / Ctrl+klik), a panel po wejściu takim adresem
 * odtwarza filtr (patrz odczyt w _renderOrdersStatusPanel). */
function _hrefFiltra(t: string, v: string, d?: string, s?: string, tg?: any[]): string {
  var q = new URLSearchParams();
  q.set('t', String(t || ''));
  q.set('v', String(v == null ? '' : v));
  if (d) q.set('d', String(d));
  if (s) q.set('s', String(s));
  if (Array.isArray(tg) && tg.length) q.set('tg', tg.map(function (x: any) { return String(x); }).join(','));
  return '/orders?' + q.toString();
}

function _wierszCzarnejListy(count: number): string {
  var aktywna = _czarnaListaAktywna();
  return '<div class="osp-item osp-grupa osp-czarna' + (aktywna ? ' active' : '') + '"' +
    ' data-filter-type="blacklist" data-filter-val="1" role="button" tabindex="0"' +
    ' title="Grupa systemowa: wszystkie zamówienia kupujących z czarnej listy, niezależnie od statusu, tagów i grup roboczych. Nie da się jej usunąć ani zmienić.">' +
    '<span style="display:inline-flex;align-items:center;gap:7px;min-width:0">' +
      '<span style="flex-shrink:0;font-size:12px;line-height:1" aria-hidden="true">\u26D4</span>' +
      '<a class="osp-grupa-nazwa" style="font-weight:700" href="' + escapeAttr(_hrefFiltra('blacklist', '1')) + '">CZARNA LISTA</a>' +
      '<span style="font-size:9px;font-weight:600;opacity:.7;letter-spacing:.04em">system</span>' +
    '</span>' +
    '<span class="osp-badge">' + Number(count || 0) + '</span></div>';
}

// 2026-09-03 (audyt): kontener tagów grupy ROBOCZEJ ma pusty status, więc stary lookup po
// data-filter-val="" nie trafiał w jej wiersz (albo trafiał w pustą grupę auto); grupa auto
// musi pasować także warunkiem płatności/wysyłki.
function _wierszGrupyDlaKontenera(panel: any, kont: any): HTMLElement | null {
  var esc = ((window as any).CSS && (window as any).CSS.escape) ? (window as any).CSS.escape : function (x: string) { return x; };
  var idG = String(kont.dataset.ospTagiGrupa || '');
  var statusG = String(kont.dataset.tagiStatus || '');
  if (!statusG && idG) {
    return panel.querySelector('.osp-grupa[data-filter-type="manual-group"][data-filter-val="' + esc(idG) + '"]');
  }
  var pd = String(kont.dataset.tagiPdims || ''), sd = String(kont.dataset.tagiSdims || '');
  var kandydaci = panel.querySelectorAll('.osp-grupa[data-filter-type="status"][data-filter-val="' + esc(statusG) + '"]');
  for (var i = 0; i < kandydaci.length; i++) {
    var el = kandydaci[i] as HTMLElement;
    if (String(el.dataset.paymentDims || '') === pd && String(el.dataset.shipmentDims || '') === sd) return el;
  }
  return (kandydaci[0] as HTMLElement) || null;
}

// Cache liczników tag×grupa (60 s). Czyszczony po nadaniu/zdjęciu tagu (wyczyscTagiGrupCache).
var _tagiGrupCache: Record<string, { t: number; tagi: any[]; sumy?: any }> = {};
var _tagiGrupPending: Record<string, Promise<any>> = {};
export function wyczyscTagiGrupCache(): void { _tagiGrupCache = {}; }
// S-139 przegląd 2: `tylkoNieswieze` — ścieżka bez przebudowy panelu odświeża chipy tylko tam, gdzie cache zniknął
// (nadanie/zdjęcie tagu) albo się zestarzał; błąd odczytu zostawia stare chipy zamiast „—”.
function _zaladujTagiGrup(panel: any, opts?: { tylkoNieswieze?: boolean }): void {
  var kontenery = panel.querySelectorAll('[data-osp-tagi-grupa]');
  if (!kontenery.length) return;
  var tok = (window as any).SellBaseAPI?.getAuthToken?.() || '';
  kontenery.forEach(function (kont: any) {
    var idG = String(kont.dataset.ospTagiGrupa || '');
    var rysuj = function (tagi: any[], podsumowanie?: any) {
      if (!tagi.length) {
        kont.innerHTML = '<span style="font-size:10px;color:var(--color-text-faint)">brak tagów</span>';
        return;
      }
      // Który tag jest AKTUALNIE użyty jako filtr — user prosił, żeby było widać,
      // że lista pokazuje właśnie ten tag. Filtr trzyma tablicę id (semantyka OR).
      var aktywne: number[] = [];
      try {
        var _af = (window as any).__sbOrdersTagFilter;
        aktywne = Array.isArray(_af) ? _af.map(function (x: any) { return parseInt(String(x), 10); }) : [];
      } catch (_e) { aktywne = []; }
      // 2026-08-25 (zgłoszenie usera): „jak zaznaczę tag w grupie W REALIZACJI,
      // to zaznacza ten tag również w innych grupach". Filtr tagów jest jeden
      // dla całej listy, ale oznaczenie „✓ aktywny" dotyczy KONKRETNEGO wiersza:
      // filtrujesz grupę + tag, więc ten sam tag pod inną grupą nie jest tym
      // samym filtrem. Bez tego pasek pokazywał zaznaczenie w kilku miejscach
      // naraz, a klik w którekolwiek z nich zmieniał grupę.
      var _idGrupyTagow = String(kont.dataset.ospTagiGrupa || '');
      var _statusGrupy = String(kont.dataset.tagiStatus || '');
      var _taGrupaAktywna =
        (!_statusGrupy && !!_idGrupyTagow && String((state as any).ordersManualGroup || '') === _idGrupyTagow)
        || (!!_statusGrupy && String((state as any).ordersStatusFilter || '') === _statusGrupy
            && String((state as any).ordersGroupPaymentDims || '') === String(kont.dataset.tagiPdims || '')
            && String((state as any).ordersGroupShipmentDims || '') === String(kont.dataset.tagiSdims || ''));
      if (!_taGrupaAktywna) aktywne = [];
      // 2026-09-08: chip to <a href="/orders?…&tg=id"> — PPM / środkowy przycisk / Ctrl+klik otwierają
      // listę z tym filtrem w nowej karcie; zwykły klik działa jak dotąd (handler niżej robi preventDefault).
      var _typChipa = _statusGrupy ? 'status' : 'manual-group';
      var _valChipa = _statusGrupy || _idGrupyTagow;
      var _pdChipa = String(kont.dataset.tagiPdims || ''), _sdChipa = String(kont.dataset.tagiSdims || '');
      kont.innerHTML = tagi.map(function (t: any) {
        var czyAktywny = aktywne.indexOf(parseInt(String(t.id), 10)) >= 0;
        return '<a class="ptag ptag--' + escapeAttr(String(t.color || 'slate')) + ' osp-tag-chip' +
          (czyAktywny ? ' osp-tag-chip--aktywny' : '') + '"' +
          ' href="' + escapeAttr(_hrefFiltra(_typChipa, _valChipa, _pdChipa, _sdChipa, [t.id])) + '"' +
          ' data-tag-id="' + t.id + '"' +
          ' aria-pressed="' + (czyAktywny ? 'true' : 'false') + '"' +
          // Chip rozciąga się na szerokość kolumny, licznik ląduje po prawej —
          // dzięki temu nazwy i liczby układają się w równy słupek (prośba usera).
          ' style="cursor:pointer;font-size:12px;padding:3px 11px;width:100%;max-width:100%;' +
          'justify-content:space-between;gap:8px"' +
          ' title="' + (czyAktywny ? 'Filtr aktywny — kliknij, aby zdjąć' : 'Filtruj: ta grupa + tag ')
            + escapeAttr(String(t.name || '')) + '">' +
          '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' +
          (czyAktywny ? '<span aria-hidden="true" style="margin-right:4px">✓</span>' : '') +
          escapeHtml(String(t.name || '')) + '</span>' +
          '<span style="opacity:.85;font-variant-numeric:tabular-nums;font-weight:700">' +
          Number(t.count || 0) + '</span></a>';
      }).join('');
      // 2026-08-25 (zgłoszenie usera: „tutaj się coś nie zgadza, podejrzewam że
      // jedno zamówienie może być w kilku tagach"). Dokładnie tak: tagi to
      // relacja wiele-do-wielu. Zmierzone na produkcji: 47 804 zamówień z
      // tagami, z czego 10 860 ma dwa lub więcej. Do tego zamówienia BEZ tagu
      // nie pokazują się na liście w ogóle. Dopisujemy więc jedno zdanie, żeby
      // liczby dało się przeczytać bez zgadywania.
      try {
        var _bezTagu = Number((podsumowanie && podsumowanie.untagged) || 0);
        var _suma = tagi.reduce(function (a: number, t: any) { return a + Number(t.count || 0); }, 0);
        var _wGrupie = Number((podsumowanie && podsumowanie.total) || 0);
        var _czesci: string[] = [];
        if (_bezTagu > 0) _czesci.push('bez tagu: ' + _bezTagu);
        if (_wGrupie && _suma > _wGrupie) _czesci.push('zamówienie może mieć kilka tagów');
        if (_czesci.length) {
          kont.innerHTML += '<span style="font-size:10px;color:var(--color-text-faint);' +
            'padding:2px 2px 0;line-height:1.4">' + escapeHtml(_czesci.join(' · ')) + '</span>';
        }
      } catch (_e) { /* stopka jest dodatkiem, nie warunkiem listy */ }
      kont.querySelectorAll('.osp-tag-chip').forEach(function (chip: any) {
        chip.addEventListener('click', function (this: any, ev: any) {
          // 2026-09-08: Ctrl/⌘/Shift+klik / środkowy przycisk = nowa karta (link chipa) — bez filtra tutaj.
          if (ev.ctrlKey || ev.metaKey || ev.shiftKey || (typeof ev.button === 'number' && ev.button !== 0)) { ev.stopPropagation(); return; }
          ev.preventDefault();
          ev.stopPropagation();
          // Filtr = GRUPA + TAG naraz: tag do filtra tagów, potem klik wiersza
          // grupy wykonuje całą standardową gałąź (klucze+wymiary+reload).
          var _idTagu = parseInt(String(this.dataset.tagId || '0'), 10);
          // 2026-09-03 (audyt): stan z chipa (aria-pressed), nie z globalnego filtra — ten sam tag
          // aktywny pod inną grupą wyglądał na nieaktywny, a klik go ZDEJMOWAŁ.
          var _juzAktywny = this.getAttribute('aria-pressed') === 'true';
          (window as any).__sbOrdersTagFilter = _juzAktywny ? [] : [_idTagu];
          var wiersz = _wierszGrupyDlaKontenera(panel, kont);
          // Zdjęcie tagu (drugie kliknięcie) ma zachowywać się jak klik w grupę,
          // więc flagę stawiamy tylko wtedy, gdy tag WCHODZI do filtra.
          _filtrZChipaTagu = !_juzAktywny;
          if (wiersz) {
            (wiersz as HTMLElement).click();
          } else {
            _filtrZChipaTagu = false;
            if (typeof (window as any).loadAllegroOrdersIntoPanel === 'function') {
              (window as any).loadAllegroOrdersIntoPanel(true);
            }
          }
        });
      });
    };
    var _c = _tagiGrupCache[idG];
    if (_c && (Date.now() - _c.t) < 60000) { if (!(opts && opts.tylkoNieswieze && kont.childElementCount)) rysuj(_c.tagi, (_c as any).sumy); return; }
    // 2026-09-03 (audyt): jeden fetch per grupa naraz (kolejne rendery w trakcie ładowania
    // dołączają do trwającej obietnicy); błąd HTTP nie jest cache'owany ani rysowany jako „brak tagów".
    var _p = _tagiGrupPending[idG];
    if (!_p) {
      _p = fetch('/api/orders/group-tag-counts?group_id=' + encodeURIComponent(idG),
            { headers: { Authorization: 'Bearer ' + tok } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (d: any) {
          var tagi = (d && Array.isArray(d.tags)) ? d.tags : [];
          var sumy = { total: Number((d && d.total) || 0), untagged: Number((d && d.untagged) || 0) };
          _tagiGrupCache[idG] = { t: Date.now(), tagi: tagi, sumy: sumy } as any;
          return { tagi: tagi, sumy: sumy };
        })
        .finally(function () { delete _tagiGrupPending[idG]; });
      _tagiGrupPending[idG] = _p;
    }
    _p.then(function (wyn: any) { rysuj(wyn.tagi, wyn.sumy); })
      .catch(function () {
        if (opts && opts.tylkoNieswieze && kont.childElementCount) return;
        kont.innerHTML = '<span style="font-size:10px;color:var(--color-text-faint)">—</span>';
      });
  });
}

/** Ikona grupy: trzy nalozone warstwy = „kilka statusow pod jedna nazwa".
 *
 *  Rysowana jako SVG WPROST, nie przez lucide — ten panel nie hydratuje `data-lucide`
 *  (swoj chevron sekcji tez ma inline), a ikona spoza wycietego zestawu lucide po
 *  cichu nie wstaje. Zaden atrybut nie zalezy tu od zewnetrznego zestawu. */
// 2026-09-03 (audyt): kolor grupy/statusu wchodzi do style/stroke — bez sita był kanałem
// stored XSS (kolor „#fff"><img onerror=…" z edycji grupy). Tylko #rgb / #rrggbb(aa).
function _kolorBezpieczny(v: any): string {
  var s = String(v || '').trim();
  return /^#[0-9a-f]{3,8}$/i.test(s) ? s : '#6b7280';
}
function _ikonaGrupy(kolor: string): string {
  return '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="' + _kolorBezpieczny(kolor) + '"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' +
    ' style="flex-shrink:0" aria-hidden="true">' +
    '<polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>' +
    '<polyline points="2 17 12 22 22 17"></polyline>' +
    '<polyline points="2 12 12 17 22 12"></polyline>' +
    '</svg>';
}

// Lazy delegates (cross-module)
function _ensureStatusTreeMap(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._ensureStatusTreeMap) || window._ensureStatusTreeMap) as any).apply(this, args); }
function loadAllegroOrdersIntoPanel(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.loadAllegroOrdersIntoPanel) || window.loadAllegroOrdersIntoPanel) as any).apply(this, args); }
function renderOrdersView(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.renderOrdersView) || (window as any).renderOrdersView) as any).apply(this, args); }

/* -----------------------------------------------
     Status Panel
     ----------------------------------------------- */
// Lazy refresh PS orderów z pustym delivery_address_json (z fast_mode sync).
// Wywołane po render strony /orders — dla widocznych PS orderów z brakiem adresu uruchamia batch refresh w tle.
var _psRefreshInProgress = false;
var _psRefreshedIds = new Set();
export async function _lazyRefreshVisiblePSOrders(paginatedOrders: any) {
    if (_psRefreshInProgress) return;
    if (!Array.isArray(paginatedOrders)) return;
    var needIds: Array<string | number> = [];
    paginatedOrders.forEach(function(o: any) {
      if (!o || !o.is_prestashop) return;
      if (_psRefreshedIds.has(o.id)) return;
      var addr = (o.shippingAddress || {});
      var phone = o.customerPhone || '';
      // Brak adresu LUB brak telefonu → potrzebuje refresh
      if (!addr.street && !phone) {
        needIds.push(o.id);
        _psRefreshedIds.add(o.id); // zaznacz że już próbowano (żeby nie zapętlić)
      }
    });
    if (!needIds.length) return;
    _psRefreshInProgress = true;
    try {
      // Backend limit 50
      var batch = needIds.slice(0, 50);
      await SellBaseAPI.batchRefreshPSOrders(batch);
      // Po zakończeniu — rehydrate całą listę
      if (typeof loadAllegroOrdersIntoPanel === 'function') {
        await loadAllegroOrdersIntoPanel(true);
      }
    } catch (e: any) { /* tolerancyjne */ }
    finally { _psRefreshInProgress = false; }
}

// Pobiera listę podłączonych integracji (Allegro + PrestaShop + Erli + eBay) i zapisuje do state.connectedIntegrations
// żeby panel Źródło pokazywał nawet integracje bez zamówień, i nie pokazywał tych bez żadnego konta.
export async function _refreshConnectedIntegrations() {
    if (!((state as any).connectedIntegrations)) (state as any).connectedIntegrations = { keys: [], accounts: {} };
    try {
      var results = await Promise.all([
        SellBaseAPI.getAccounts().catch(function() { return {accounts:[]}; }),
        SellBaseAPI.getPSAccounts().catch(function() { return {accounts:[]}; }),
        SellBaseAPI.getErliAccounts().catch(function() { return {accounts:[]}; }),
        SellBaseAPI.getEbayAccounts().catch(function() { return {accounts:[]}; }),
        SellBaseAPI.getTiktokAccounts().catch(function() { return {accounts:[]}; }),
        SellBaseAPI.getVonhalskyAccounts().catch(function() { return {accounts:[]}; })
      ]);
      var allegroAccounts = ((results[0] && (results[0].accounts || results[0])) || []).filter(function(a: IntegrationAccount) { return a && a.is_active !== 0 && a.is_active !== false; });
      var psAccounts = ((results[1] && results[1].accounts) || []).filter(function(a: IntegrationAccount) { return a && a.is_active; });
      var erliAccounts = ((results[2] && results[2].accounts) || []).filter(function(a: IntegrationAccount) { return a && a.is_active; });
      var ebayAccounts = ((results[3] && results[3].accounts) || []).filter(function(a: IntegrationAccount) { return a && a.is_active; });
      var tiktokAccounts = ((results[4] && results[4].accounts) || []).filter(function(a: IntegrationAccount) { return a && a.is_active; });
      var vonhalskyAccounts = ((results[5] && results[5].accounts) || []).filter(function(a: IntegrationAccount) { return a && a.is_active; });
      var keys = [];
      if (allegroAccounts.length) keys.push('allegro');
      if (psAccounts.length) keys.push('prestashop');
      if (erliAccounts.length) keys.push('erli');
      if (ebayAccounts.length) keys.push('ebay');
      if (tiktokAccounts.length) keys.push('tiktok');
      if (vonhalskyAccounts.length) keys.push('vonhalsky');
      (state as any).connectedIntegrations = {
        keys: keys,
        accounts: { allegro: allegroAccounts, prestashop: psAccounts, erli: erliAccounts, ebay: ebayAccounts, tiktok: tiktokAccounts, vonhalsky: vonhalskyAccounts }
      };
      // Gdy panel jest widoczny, odśwież filtr (bez pełnego rerendera)
      if ((state as any).currentView === 'orders' && typeof _renderOrdersStatusPanel === 'function') {
        _renderOrdersStatusPanel();
      }
    } catch (e: any) { /* tolerancyjne */ }
}

// Domyślnie rozwinięte są tylko Statusy; Płatność/Przesyłka/Źródło są zwinięte do pierwszego kliknięcia
// 2026-05-20: source defaultowo OTWARTE — user wymaga widoczności kont Allegro od razu po wejściu.
var _OSP_DEFAULT_COLLAPSED: Record<string, boolean> = { statuses: false, payment: true, shipment: true, source: false };
// 0245: chevronowe sekcje też per UŻYTKOWNIK — na wspólnym stanowisku (pakowanie)
// jeden profil przeglądarki obsługuje kilku zalogowanych, więc goły klucz
// 'osp_collapsed' przeciekał między kontami. Klucz z id usera; stary klucz
// czytamy jako fallback (nikt nie traci swojego układu po wdrożeniu).
function _ospKluczMagazynu(): string {
  try {
    var u: any = (window as any).__sbState && (window as any).__sbState.currentUser;
    var uid = u && u.id;
    if (uid) return 'osp_collapsed:' + String(uid);
  } catch (_e) { /* brak usera → klucz wspólny (stan sprzed 0245) */ }
  return 'osp_collapsed';
}
export function _ospIsCollapsed(key: string) {
    try {
      var raw = localStorage.getItem(_ospKluczMagazynu()) || localStorage.getItem('osp_collapsed');
      if (!raw) return !!_OSP_DEFAULT_COLLAPSED[key];
      var set = JSON.parse(raw) || {};
      if (Object.prototype.hasOwnProperty.call(set, key)) return !!set[key];
      return !!_OSP_DEFAULT_COLLAPSED[key];
    } catch (e: any) { return !!_OSP_DEFAULT_COLLAPSED[key]; }
}
export function _ospSetCollapsed(key: string, collapsed: boolean) {
    try {
      var magazyn = _ospKluczMagazynu();
      var raw = localStorage.getItem(magazyn) || localStorage.getItem('osp_collapsed');
      var set = raw ? (JSON.parse(raw) || {}) : {};
      // Zawsze zapisujemy jawnie, żeby odróżnić "user świadomie rozwinął" od "nie dotknięto"
      set[key] = collapsed ? 1 : 0;
      localStorage.setItem(magazyn, JSON.stringify(set));
    } catch (e: any) {}
}
export function _ospSectionHtml(key: string, title: string, bodyHtml: string) {
    var closed = _ospIsCollapsed(key);
    var chevTransform = closed ? 'rotate(-90deg)' : 'rotate(0deg)';
    return '<div class="osp-section-title osp-collapsible' + (closed ? ' osp-closed' : '') + '" data-osp-section="' + escapeAttr(key) + '" style="display:flex;align-items:center;justify-content:space-between;cursor:pointer;user-select:none">' +
             '<span>' + escapeHtml(title) + '</span>' +
             '<svg class="osp-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.6;transition:transform .18s ease;transform:' + chevTransform + '"><polyline points="6 9 12 15 18 9"></polyline></svg>' +
           '</div>' +
           '<div class="osp-section-body' + (closed ? ' osp-closed' : '') + '" data-osp-body="' + escapeAttr(key) + '" style="' + (closed ? 'display:none' : '') + '">' + bodyHtml + '</div>';
}

/** 2026-08-17: WSPÓLNY tor filtra pozycji „status" / „manual-group" paska.
 *
 *  Woła go i delegowany klik wiersza (grupa, status, status w grupie, „Wszystkie"),
 *  i auto-wybór pierwszej grupy po wejściu na /orders — jedno miejsce mutacji stanu,
 *  więc podświetlenie, liczniki i fetch listy działają identycznie niezależnie od
 *  tego, kto „kliknął". ZERO duplikacji logiki stanu (decyzja usera 2026-08-17).
 */
// Klik w chip tagu wywołuje `wiersz.click()` na grupie, żeby przejść całą standardową
// gałąź filtrowania. Ta flaga odróżnia takie wejście od kliknięcia w samą nazwę grupy —
// tylko to drugie ma zdejmować filtr tagu.
var _filtrZChipaTagu = false;
// 2026-09-03 (audyt): jedno miejsce zerowania filtrów paska — woła je też router przy wejściu
// na Zamówienia (preset źródła z zakładek marketplace nie zerował grupy roboczej, tagów ani
// czarnej listy, więc leciały razem z source=prestashop).
export function resetujFiltryPaska(): void {
  (state as any).ordersManualGroup = null;
  (state as any).ordersGroupPaymentDims = '';
  (state as any).ordersGroupShipmentDims = '';
  (state as any).ordersGroupBezRob = false;
  (state as any).ordersProductionStatus = '';
  (state as any).ordersProductionWorker = '';
  try { (window as any).__sbOrdersTagFilter = []; } catch (_e) { /* noop */ }
  _filtrZChipaTagu = false;
  try {
    var _ot = (window as any).__sbOrderTags;
    if (_ot && _ot.ustawFiltrCzarnejListy) _ot.ustawFiltrCzarnejListy(false);
  } catch (_e2) { /* filtr systemowy jest dodatkiem */ }
}
// 2026-09-03 (audyt): tag przełączony w sekcji „Tagi zamówień" nie trafiał do zapisu paska —
// po F5 wracał stary zestaw tagów z ostatniego kliku w grupę.
export function zapiszTagiDoFiltraPaska(): void {
  try {
    var _z = sessionStorage.getItem('sb_orders_filtr_paska');
    if (!_z) return;
    var _f = JSON.parse(_z);
    var _t: any = (window as any).__sbOrdersTagFilter;
    _f.tg = Array.isArray(_t) ? _t : [];
    sessionStorage.setItem('sb_orders_filtr_paska', JSON.stringify(_f));
  } catch (_e) { /* tryb prywatny */ }
}
function zastosujFiltrGrupy(ftype: string, fval: string, dimsAttr: string,
                            shipsAttr: string): void {
    // 2026-09-02: klik w grupę podczas wyników ze wszystkich grup = „szukaj tego
    // w TEJ grupie": fraza ZOSTAJE, gaśnie tylko tryb wszystkich grup (dawniej
    // fraza była czyszczona, co user odbierał jako gubienie wyszukiwania).
    if (isOrdersGlobalSearchActive()) {
      (state as any).ordersSearchAllGroups = false;
      (state as any).ordersSearchScopeAuto = false;
      // 2026-09-03 (audyt): zapamiętaj frazę, dla której user świadomie wybrał zakres grupy —
      // pusta wartość ponownie uzbrajała auto-przejście do „wszystkich grup" i grupa z 0 trafień
      // natychmiast wracała do pełnego zakresu.
      (state as any).__ordersSearchFallbackQuery = String((state as any).ordersFilterQuery || '').trim();
      syncOrdersSearchScopeUI();
    }
    // 2026-08-21 (prośba usera): „dobrze by było, jakbym mógł kliknąć w nazwę grupy,
    // aby pokazało wszystkie zamówienia z grupy, a nie tylko z tagu". Klik w grupę
    // czyści więc filtr tagu — poza sytuacją, gdy to właśnie chip tagu wywołał ten
    // filtr (wtedy tag ma zostać).
    if (!_filtrZChipaTagu) {
      try { (window as any).__sbOrdersTagFilter = []; } catch (_e) { /* filtr i tak zostanie przeliczony */ }
    }
    _filtrZChipaTagu = false;
    // 2026-08-27 (zgłoszenie usera): wybrana grupa ma przeżyć F5 — zapis per
    // karta (sessionStorage); auto-wybór pierwszej grupy przywraca ten wybór
    // zamiast pierwszej grupy z góry paska.
    try {
      // 2026-08-27 v2 (zgłoszenie usera): tag (np. P4U Sklep) też ma przeżyć F5 —
      // zapisujemy go RAZEM z grupą (chip idzie torem klik-w-grupę, więc tu jest
      // już rozstrzygnięte, czy tag zostaje, czy spadł).
      var _tagiTeraz: any = (window as any).__sbOrdersTagFilter;
      sessionStorage.setItem('sb_orders_filtr_paska',
        JSON.stringify({ t: ftype, v: String(fval || ''), d: dimsAttr || '', s: shipsAttr || '',
                         tg: Array.isArray(_tagiTeraz) ? _tagiTeraz : [] }));
    } catch (_ePer) { /* tryb prywatny — wybór po F5 wróci do domyślnego */ }
    // 0232: kazdy wybor resetuje filtry kubelkowe; galaz statusu ustawia je na nowo.
    (state as any).ordersGroupPaymentDims = '';
    (state as any).ordersGroupShipmentDims = '';
    (state as any).ordersGroupBezRob = false;
    (state as any).ordersManualGroup = null;
    (state as any).ordersProductionStatus = '';
    (state as any).ordersProductionWorker = '';
    // Grupa systemowa CZARNA LISTA: własny filtr (`blacklist=1`, wszystkie statusy);
    // każdy inny klik w panelu go zdejmuje.
    try {
      var _otBl = (window as any).__sbOrderTags;
      if (_otBl && _otBl.ustawFiltrCzarnejListy) _otBl.ustawFiltrCzarnejListy(ftype === 'blacklist');
    } catch (_eBl) { /* filtr systemowy jest dodatkiem */ }
    if (ftype === 'blacklist') {
      (state as any).ordersStatusFilter = 'all';
    } else if (ftype === 'manual-group-production') {
      // PRODUKCJA v2: przecięcie grupa∩stan-produkcji∩osoba.
      // fval = "gid|status|<worker URI-enkodowany>" — osoba może mieć '|' w
      // nazwie, więc bierzemy resztę OD drugiego separatora i dekodujemy.
      // Pusty worker = wiersz „bez osoby" → sentinel '__bez__' dla backendu
      // (bez niego filtr status-only pokazywał też pozycje Z osobami).
      var _raw = String(fval);
      var _i1 = _raw.indexOf('|');
      var _i2 = _raw.indexOf('|', _i1 + 1);
      var _wrk = '';
      try { _wrk = decodeURIComponent(_raw.slice(_i2 + 1)); } catch (_e) { _wrk = _raw.slice(_i2 + 1); }
      (state as any).ordersManualGroup = parseInt(_raw.slice(0, _i1), 10) || null;
      (state as any).ordersProductionStatus = _raw.slice(_i1 + 1, _i2) || '';
      (state as any).ordersProductionWorker = _wrk || '__bez__';
      (state as any).ordersStatusFilter = 'all';
    } else if (ftype === 'manual-group') {
      // 2026-08-21 (zgłoszenie usera): drugie kliknięcie w tę samą grupę ZDEJMOWAŁO
      // filtr (toggle) i lista pokazywała nagle wszystko — wyglądało to na przypadkowe
      // odznaczenie, bo nazwa grupy dalej była podświetlona przez moment.
      // Teraz klik w grupę ZAWSZE ją wybiera, niezależnie od liczby kliknięć.
      // Wyjście z grupy odbywa się przez wybranie innej pozycji paska albo „Wszystkie".
      (state as any).ordersManualGroup = parseInt(String(fval), 10) || null;
      (state as any).ordersStatusFilter = 'all';
    } else if (ftype === 'manual-group-status') {
      // U#2: klik w status ZAWARTOŚCI grupy roboczej = przecięcie grupa∩status
      // (fval = "gid|klucz"; listing składa oba filtry niezależnie).
      var _mgs = String(fval).split('|');
      (state as any).ordersManualGroup = parseInt(_mgs[0], 10) || null;
      (state as any).ordersStatusFilter = _mgs[1] || 'all';
    } else if (fval === 'all') {
      (state as any).ordersStatusFilter = 'all';
    } else {
      (state as any).ordersStatusFilter = fval;
      // GRUPY 2.0: filtr grupy/przeciecia = klucze + wymiar platnosci
      // + wymiar WYSYLKI + pierwszenstwo grup recznych (wykluczenie
      // przypisanych). Wiersz statusu w grupie niesie te same atrybuty,
      // wiec klik filtruje dokladnie przeciecie z licznika.
      if (dimsAttr) (state as any).ordersGroupPaymentDims = dimsAttr;
      if (shipsAttr) (state as any).ordersGroupShipmentDims = shipsAttr;
      if (dimsAttr || shipsAttr) (state as any).ordersGroupBezRob = true;
    }
    (state as any).ordersSourceFilter = 'all';
    (state as any).ordersPaymentFilter = 'all';
    (state as any).ordersShipmentFilter = 'all';
    (state as any).ordersAccountFilter = null;
    (state as any).currentPage.orders = 1;
    // Sprint 25 Faza 5 (2026-05-03): zmiana filtra → server fetch (nie tylko slice).
    // Backend zwraca prawidłową stronę z filtrem applied; frontend nie filtruje lokalnie.
    if (typeof loadAllegroOrdersIntoPanel === 'function') {
      loadAllegroOrdersIntoPanel(true);
    } else {
      renderOrdersView();
    }
}

// ── AUTO-WYBÓR PIERWSZEJ GRUPY (2026-08-17, decyzja usera) ──
// Po wejściu na /orders bez presetu źródła aktywna ma być PIERWSZA GRUPA OD GÓRY
// paska (kolejność z ustawień), nie „Wszystkie". Router (legacy-router-utils.ts)
// zostawia one-shot flagę `__sbOrdersAutoPierwszaGrupa` i POMIJA swój fetch listy —
// fetch wykonuje dopiero zastosujFiltrGrupy, więc nie ma podwójnego pobrania.
//
// PUŁAPKA rerenderów: panel rysuje się wielokrotnie (filtry, sync, liczniki) —
// auto-wybór wykonuje się WYŁĄCZNIE pod flagą i gasi ją przy konsumpcji
// (route order-detail i powroty z drawera NIE przełączają widoku).
var _autoGrupaTimer: any = null;
// P4 (recenzja P4-POPR-6, 2026-09-15): link z karty „Automat dokumentów sprzedaży” niesie stan dokumentu
// (document_state=missing) — ustawiamy go jak filtr zaawansowany listy (backend: document_state w listingu).
function _stanDokumentuZAdresu(q: URLSearchParams): void {
  var stan = String(q.get('document_state') || '');
  if (/^(issued|missing|draft|cancelled)$/.test(stan)) (state as any).ordersFilterDocumentState = stan;
}

function _autoWybierzPierwszaGrupe(panel: HTMLElement): void {
    var w: any = window as any;
    if (!w.__sbOrdersAutoPierwszaGrupa) return;
    // Grupy mogą dojechać z sieci już po tym, jak user zaczął pisać. Auto-wybór
    // nie jest świadomym kliknięciem i nie może wyczyścić aktywnej frazy.
    // 19.10.2026: flaga GAŚNIE — inaczej czekała uzbrojona i po wyczyszczeniu frazy najbliższe przerysowanie
    // panelu (np. po przeniesieniu zamówień do innej grupy) przestawiało widok na zapamiętaną / pierwszą grupę.
    if (isOrdersGlobalSearchActive()) {
      var _fetchCzekal = !!w.__sbOrdersAutoGrupaFetchPominiety;
      w.__sbOrdersAutoPierwszaGrupa = false;
      w.__sbOrdersAutoGrupaFetchPominiety = false;
      // router pominął pobranie listy (ciepłe wejście) w zamian za auto-wybór — teraz pobieramy ją sami
      if (_fetchCzekal && typeof loadAllegroOrdersIntoPanel === 'function') loadAllegroOrdersIntoPanel(true);
      return;
    }
    // Szczegół zamówienia dzieli widok 'orders' — auto-wybór nie może przestawić
    // filtrów pod otwartą szufladą. Flaga gaśnie bez zmiany widoku (hydrację listy
    // w tle zapewnia route order-detail).
    if ((state as any).selectedOrderId || (state as any).ordersDrawerOpen) {
      w.__sbOrdersAutoPierwszaGrupa = false;
      return;
    }
    // Grupy przychodzą z sieci (fetch przy _ensureStatusTreeMap). REVIEW
    // 2026-08-17 (F3): router NIE pomija już fetchu listy na zimnym wejściu —
    // „Wszystkie" ładuje się natychmiast, a flaga czeka na rerender po dojściu
    // grup. Timer jest tylko wygaszaczem (clear-only): po 5 s bez grup flaga
    // gaśnie BEZ akcji, żeby późny rerender (sync/liczniki) nie przełączył
    // userowi widoku znienacka.
    var _grupyRaw = (window.__sbStateExt && (window.__sbStateExt as any)._statusGroups);
    if (!Array.isArray(_grupyRaw)) {
      if (_autoGrupaTimer === null) {
        _autoGrupaTimer = setTimeout(function () {
          _autoGrupaTimer = null;
          w.__sbOrdersAutoPierwszaGrupa = false;
        }, 5000);
      }
      return;
    }
    if (_autoGrupaTimer !== null) { clearTimeout(_autoGrupaTimer); _autoGrupaTimer = null; }
    // Flaga gaśnie PRZED fetchem — rerender panelu w środku toru nie odpali
    // auto-wyboru drugi raz (zużycie JEDNORAZOWE).
    w.__sbOrdersAutoPierwszaGrupa = false;
    var fetchPominiety = !!w.__sbOrdersAutoGrupaFetchPominiety;
    w.__sbOrdersAutoGrupaFetchPominiety = false;
    // 2026-08-27 (zgłoszenie usera): F5 przestawiał na PIERWSZĄ grupę, choć user
    // stał na innej (np. ZMONTOWANE). Najpierw próbujemy przywrócić zapamiętany
    // wybór — wiersz szukany w NARYSOWANYM panelu, żeby przejść dokładnie tor
    // kliknięcia (dataset niesie payment/shipment dims). Braku wiersza (grupa
    // usunięta) nie udajemy — wtedy standardowo pierwsza grupa.
    try {
      // 2026-09-08: filtr z adresu (/orders?t=…&v=…[&d][&s][&tg]) — link paska otwarty w nowej karcie
      // (PPM / środkowy przycisk / Ctrl+klik). Zapisujemy go jak klik i czyścimy adres, żeby F5 nie
      // wskrzeszał go po późniejszej zmianie wyboru.
      var _zapis: string | null = null;
      try {
        var _q = new URLSearchParams(String(location.search || ''));
        if (String(location.pathname || '').indexOf('/orders') === 0 && _q.get('t') && _q.get('v') !== null) {
          var _tgUrl = String(_q.get('tg') || '').split(',').map(function (x: string) { return parseInt(x, 10); })
            .filter(function (n: number) { return !isNaN(n); });
          _zapis = JSON.stringify({ t: _q.get('t'), v: _q.get('v') || '', d: _q.get('d') || '', s: _q.get('s') || '', tg: _tgUrl });
          sessionStorage.setItem('sb_orders_filtr_paska', _zapis);
          history.replaceState(null, '', '/orders');
        }
        _stanDokumentuZAdresu(_q);
      } catch (_eUrl) { /* parametry adresu to udogodnienie */ }
      if (!_zapis) _zapis = sessionStorage.getItem('sb_orders_filtr_paska');
      if (_zapis) {
        var _f = JSON.parse(_zapis);
        if (_f && _f.t && String(_f.v || '') !== '') {
          var _wszystkieWiersze = panel.querySelectorAll<HTMLElement>('.osp-item');
          for (var _j = 0; _j < _wszystkieWiersze.length; _j++) {
            var _el2 = _wszystkieWiersze[_j] as HTMLElement;
            if (String(_el2.dataset.filterType || '') === String(_f.t) &&
                String(_el2.dataset.filterVal || '') === String(_f.v) &&
                (_f.d === undefined || String(_el2.dataset.paymentDims || '') === String(_f.d || '')) &&
                (_f.s === undefined || String(_el2.dataset.shipmentDims || '') === String(_f.s || ''))) {
              // 2026-08-27 v2: tag zapisany z grupą — odtwórz PRZED torem kliku
              // i podnieś flagę chipa, żeby zastosujFiltrGrupy go nie wyczyścił.
              if (Array.isArray(_f.tg) && _f.tg.length) {
                (w as any).__sbOrdersTagFilter = _f.tg;
                _filtrZChipaTagu = true;
              }
              zastosujFiltrGrupy(String(_f.t), String(_f.v),
                String(_el2.dataset.paymentDims || _f.d || ''),
                String(_el2.dataset.shipmentDims || _f.s || ''));
              return;
            }
          }
          // 2026-09-03 (audyt): wiersz produkcji / statusu w grupie może jeszcze nie istnieć
          // (stats i prefs dochodzą osobnym fetchem) — nie oddawaj wyboru pierwszej grupie,
          // poczekaj na kolejny render (max 5 prób); wcześniej zapamiętany filtr ginął po F5.
          if ((_f.t === 'manual-group-production' || _f.t === 'manual-group-status') &&
              Number(w.__sbOrdersRestoreProby || 0) < 5) {
            w.__sbOrdersRestoreProby = Number(w.__sbOrdersRestoreProby || 0) + 1;
            w.__sbOrdersAutoPierwszaGrupa = true;
            return;
          }
        }
      }
    } catch (_ePer) { /* uszkodzony zapis → standardowy auto-wybór */ }
    // Pierwszy wiersz `.osp-grupa` W KOLEJNOŚCI RENDERU = pierwsza grupa od góry
    // paska (sort_order z ustawień; reczne i warunkowe w jednej liście). Czytamy
    // jego dataset — dokładnie to, co czyta delegowany klik wiersza.
    // REVIEW 2026-08-17 (F6): pomijamy wiersze z PUSTYM filtrem (świeżo założona
    // grupa warunkowa bez skutecznych kluczy) — fval '' ustawiałby
    // ordersStatusFilter='' i panel zostawał bez aktywnej pozycji.
    var pierwsza: HTMLElement | null = null;
    var wiersze = panel.querySelectorAll('.osp-item.osp-grupa');
    for (var i = 0; i < wiersze.length; i++) {
      var el = wiersze[i] as HTMLElement;
      if (String(el.dataset.filterVal || '') !== '') { pierwsza = el; break; }
    }
    if (pierwsza) {
      zastosujFiltrGrupy(String(pierwsza.dataset.filterType || 'status'),
        String(pierwsza.dataset.filterVal || ''),
        String(pierwsza.dataset.paymentDims || ''),
        String(pierwsza.dataset.shipmentDims || ''));
    } else if (fetchPominiety) {
      // Firma bez (użytecznych) grup, a router pominął swój fetch (ciepłe
      // wejście) — ładujemy „Wszystkie" tym samym torem co klik.
      zastosujFiltrGrupy('status', 'all', '', '');
    }
    // Firma bez grup przy ZIMNYM wejściu: router już załadował listę — nic nie robimy.
}

export function _renderOrdersStatusPanel() {
    var panel = document.getElementById('orders-status-panel');
    if (!panel) return;

    // Załaduj mapę statusów SellBase (asynchronicznie — jeśli jeszcze nie ma, doładujemy i ponownie zrenderujemy)
    if (typeof _ensureStatusTreeMap === 'function') _ensureStatusTreeMap();

    var orders = SellBaseData.orders || [];

    // Count per status
    var statusCounts: any = {};
    var sourceCounts: any = {};
    var paymentStatusCounts: any = {}; // key -> count (po statusie płatności SellBase)
    var codCount = 0;
    var shipmentCounts: any = {};

    orders.forEach(function(rawOrder: any) {
      var o: any = rawOrder;
      var sl = o.status ? (o.status.label || '') : '';
      if (sl) statusCounts[sl] = (statusCounts[sl] || 0) + 1;
      var src = o.source ? (o.source.key || '') : '';
      if (src) sourceCounts[src] = (sourceCounts[src] || 0) + 1;
      // Payment
      var pay: any = o.payment || {};
      var payStatus = String(typeof pay.status === 'object' ? (pay.status.key || '') : (pay.status || '')).toLowerCase();
      var payType = String(pay.type || pay.raw_status || '').toUpperCase();
      if (!payStatus) payStatus = 'pending';
      // Aliasy legacy: unpaid/not_paid/new -> pending
      if (payStatus === 'unpaid' || payStatus === 'not_paid' || payStatus === 'new') payStatus = 'pending';
      paymentStatusCounts[payStatus] = (paymentStatusCounts[payStatus] || 0) + 1;
      if (payType.indexOf('CASH_ON_DELIVERY') !== -1 || payType.indexOf('COD') !== -1) codCount++;
      // Shipment
      var shipSt = String(o.shipment_status || 'no_shipment').toLowerCase();
      shipmentCounts[shipSt] = (shipmentCounts[shipSt] || 0) + 1;
    });

    // Sprint 25 (2026-05-03): nadpisz STATUS counts z server stats endpoint jeśli dostępne.
    // Eliminuje migotanie liczb przy wejściu na /orders gdy stage 2 prefetch jeszcze trwa.
    // Sprint 25 #6 (2026-05-03): nadpisz też payment/shipment/source/cod gdy serwer dostarcza
    // — bez tego liczyło się tylko z 50 zamówień bieżącej strony (paginacja serwerowa).
    var serverStats = (state as any).ordersServerStats;
    var totalAll = orders.length;
    if (serverStats && typeof serverStats.all === 'number') {
      if (serverStats.by_label) {
        statusCounts = Object.assign({}, serverStats.by_label);
      }
      totalAll = serverStats.all;
      if (serverStats.by_payment && typeof serverStats.by_payment === 'object') {
        paymentStatusCounts = Object.assign({}, serverStats.by_payment);
      }
      if (serverStats.by_shipment && typeof serverStats.by_shipment === 'object') {
        shipmentCounts = Object.assign({}, serverStats.by_shipment);
      }
      if (serverStats.by_source && typeof serverStats.by_source === 'object') {
        sourceCounts = Object.assign({}, serverStats.by_source);
      }
      if (typeof serverStats.cod_count === 'number') {
        codCount = serverStats.cod_count;
      }
    }

    // ─── WSZYSTKIE ─── (standalone, nie jest statusem)
    // 2026-09-03 (audyt): grupa robocza / wiersz produkcji / CZARNA LISTA ustawiają status 'all',
    // więc „Wszystkie" świeciło razem z nimi.
    var allActive = (state as any).ordersStatusFilter === 'all' && (state as any).ordersSourceFilter === 'all' && (state as any).ordersPaymentFilter === 'all' && ((state as any).ordersShipmentFilter || 'all') === 'all'
      && !(state as any).ordersManualGroup && !(state as any).ordersProductionStatus && !_czarnaListaAktywna();
    var allItemHtml = '<div class="osp-all-wrap"><div class="osp-item osp-item-all' + (allActive ? ' active' : '') + '" data-filter-type="status" data-filter-val="all">' +
      '<span>Wszystkie</span><span class="osp-badge">' + totalAll + '</span></div></div>';

    // ─── STATUSY ───
    var statusBody = '';
    // ETAP 3 (2026-08-10): pasek stoi na KLUCZACH, nie na nazwach.
    //
    // CO BYŁO ZŁE: wiersze brał z drzewka po NAZWIE, licznik szukał po NAZWIE
    // (`statusCounts[label]`), filtr wysyłał NAZWĘ, a kolejność liczył z NAZWY.
    // Napisy w danych to nazwy KANONICZNE („Spakowane"), a nazwa w drzewku jest tym,
    // co user może zmienić. Dopóki są równe, wszystko trafia w siebie. Po pierwszym
    // przemianowaniu status pokazywał LICZNIK 0 mimo tysięcy zamówień, klik dawał
    // pustą listę, a wiersz spadał na koniec paska (priorytet 99). Bez błędu.
    var STATUS_ORDER_PRIORITY: Record<string, number> = {
      'new': 0,
      'shipped': 1,
      'picked_up': 2, 'delivered': 2,
      'packed': 3,
      'cancelled': 4,
      'returned': 5,
    };
    function _stPriority(key: string): number {
      var k = String(key || '').toLowerCase().trim();
      return (k in STATUS_ORDER_PRIORITY) ? STATUS_ORDER_PRIORITY[k] : 99;
    }
    var _kluczStatusu = ((window as any).__sbExports && (window as any).__sbExports.kluczStatusu)
      || function (v: any) { return String(v || '').toLowerCase(); };
    var _etykietaStatusu = ((window as any).__sbExports && (window as any).__sbExports.etykietaStatusu)
      || function (_k: string, n: string) { return n; };
    // Licznik po KLUCZU — `by_key` z `/api/orders/stats`. Gdy go nie ma (stary serwer
    // albo pierwszy render przed odpowiedzią), składamy go z `by_label` tą samą funkcją
    // klucza, żeby pasek nigdy nie pokazał zera tam, gdzie liczby są.
    var statusCountsByKey: Record<string, number> = {};
    if (serverStats && serverStats.by_key && typeof serverStats.by_key === 'object') {
      statusCountsByKey = Object.assign({}, serverStats.by_key);
    } else {
      Object.keys(statusCounts).forEach(function(label) {
        var k = String(_kluczStatusu(label) || '');
        if (k) statusCountsByKey[k] = (statusCountsByKey[k] || 0) + Number(statusCounts[label] || 0);
      });
    }
    // Statusy SellBase — od 2026-08-10 moga miec PIETRA: grupa -> status -> kody
    // marketplace. Bierzemy wszystko, co NIE jest mapowaniem marketplace (te maja
    // `source_key`), niezaleznie od tego, czy ma rodzica.
    var statusRows: Array<{ label: string; key: string; color: string; sort_order: number;
                            parent: string; poziom: number; dzieci: string[];
                            grupa: number }> = [];
    var seenStatusKeys: Record<string, boolean> = {};
    var orderTreeMap = (window.__sbStateExt && (window.__sbStateExt as any)._statusTreeMap) || {};
    Object.keys(orderTreeMap).forEach(function(mapKey) {
      var s = orderTreeMap[mapKey];
      if (!s || !s.key || String(s.category || 'order') !== 'order') return;
      // Mapowanie marketplace (ma `source_key`) NIE jest statusem SellBase.
      if (String(s.source_key || '').trim()) return;
      var _src = String(s.source || 'sellbase').toLowerCase();
      if (_src && _src !== 'sellbase' && _src !== 'custom') return;
      var key = String(s.key).toLowerCase();
      if (seenStatusKeys[key]) return;
      seenStatusKeys[key] = true;
      statusRows.push({
        label: String(s.name || s.key),
        key: key,
        color: s.color || '#6b7280',
        sort_order: Number(s.sort_order || 0),
        parent: '',
        poziom: 0,
        dzieci: [],
        grupa: Number(s.group_id || 0),
      });
    });
    var wgKlucza: Record<string, any> = {};
    statusRows.forEach(function(r) { wgKlucza[r.key] = r; });
    // 0232 (kubelki): przekroj klucz x wymiar platnosci (bez zamowien w grupach
    // roboczych — pierwszenstwo roboczej) + liczniki grup roboczych z serwera.
    var byKeyPay: Record<string, Record<string, number>> =
      (serverStats && (serverStats as any).by_key_payment) || {};
    // GRUPY 2.0 (0234): pelny przekroj klucz x platnosc x wysylka — liczniki grup
    // z warunkiem wysylki i liczniki PRZECIEC w rozwinieciu grupy.
    var byKeyPayShip: Record<string, Record<string, Record<string, number>>> =
      (serverStats && (serverStats as any).by_key_payment_shipment) || {};
    var manualCounts: Record<string, number> =
      (serverStats && (serverStats as any).manual_groups) || {};
    // U#2→2026-08-19 #2: rozkład statusów zawartości grup ręcznych wykodowany
    // z widoku (grupa robocza = stany PRODUKCJI); agregat w stats zostaje.
    var maPrzekroj = !!(serverStats && (serverStats as any).by_key_payment);
    // GRUPY STATUSOW (2026-08-10) — czyste szuflady. NIE sa statusami: nie maja klucza,
    // nie da sie ich ustawic na zamowieniu, nie ma ich w slowniku statusow. Sluza
    // wylacznie do porzadkowania paska.
    //
    // Jeden poziom (decyzja usera) — wiec zadnej rekurencji i zadnego ryzyka zapetlenia,
    // ktore trzeba bylo obchodzic, gdy „grupa" byla statusem z `parent_key`.
    var grupyWszystkie: Array<{ id: number; name: string; color: string; sort_order: number;
                                group_type?: string; payment_values?: string }> =
      ((window.__sbStateExt && (window.__sbStateExt as any)._statusGroups) || []) as any[];
    // GRUPY 2.0 (0234): JEDNA lista grup we wspolnej kolejnosci (koniec osobnej
    // sekcji „Grupy robocze" — decyzja usera); grupa reczna ma ikone rączki.
    // Czlonkostwo grup warunkowych WYLACZNIE ze status_keys (grupa=zdanie-warunek).
    var grupy = grupyWszystkie.slice();
    var statusyGrupy: Record<number, any[]> = {};
    var wJakiejsGrupie: Record<string, boolean> = {};
    grupy.forEach(function(g: any) {
      if (String(g.group_type || 'auto') === 'manual') return;
      String((g as any).status_keys || '').split(',').forEach(function(k: string) {
        k = k.trim().toLowerCase();
        if (!k || !wgKlucza[k]) return;
        var idG = Number(g.id);
        var lista = (statusyGrupy[idG] = statusyGrupy[idG] || []);
        if (lista.indexOf(wgKlucza[k]) === -1) lista.push(wgKlucza[k]);
        wJakiejsGrupie[k] = true;
      });
    });
    function _dimsGrupy(g: any): string[] {
      return String(g && g.payment_values || '').split(',')
        .map(function(x: string) { return x.trim().toLowerCase(); })
        .filter(function(x: string) { return !!x; });
    }
    function _shipsGrupy(g: any): string[] {
      return String(g && g.shipment_values || '').split(',')
        .map(function(x: string) { return x.trim().toLowerCase(); })
        .filter(function(x: string) { return !!x; });
    }
    /** GRUPY 2.0: przeciecie JEDNEGO statusu z warunkiem grupy — licznik
     *  „ktore Nowe jest ktore" w rozwinietej grupie. */
    function _przeciecieStatusu(klucz: string, g: any): number {
      var dims = _dimsGrupy(g), ships = _shipsGrupy(g);
      if (ships.length) {
        var w3 = byKeyPayShip[klucz] || {};
        var dimsList = dims.length ? dims : Object.keys(w3);
        return dimsList.reduce(function(su: number, d: string) {
          var wS = w3[d] || {};
          return su + ships.reduce(function(s2: number, sh: string) {
            return s2 + Number(wS[sh] || 0);
          }, 0);
        }, 0);
      }
      if (dims.length) {
        var w2 = byKeyPay[klucz] || {};
        return dims.reduce(function(su2: number, d2: string) { return su2 + Number(w2[d2] || 0); }, 0);
      }
      return Number(statusCountsByKey[klucz] || 0);
    }
    /** Suma licznika grupy AUTO. Z warunkiem platnosci liczy z przekroju
     *  klucz x wymiar (serwer wyklucza zamowienia w grupach roboczych);
     *  bez warunku — jak dotad suma statusow. */
    function _sumaGrupy(id: number, g?: any): number {
      if (g && maPrzekroj && (_dimsGrupy(g).length || _shipsGrupy(g).length)) {
        return (statusyGrupy[id] || []).reduce(function(suma: number, r: any) {
          return suma + _przeciecieStatusu(r.key, g);
        }, 0);
      }
      return (statusyGrupy[id] || []).reduce(function(suma: number, r: any) {
        return suma + Number(statusCountsByKey[r.key] || 0);
      }, 0);
    }
    /** Klik w grupe filtruje WSZYSTKIE jej statusy — licznik pokazuje sume, wiec
     *  lista musi pokazac dokladnie tyle. */
    function _kluczeGrupy(id: number): string[] {
      return (statusyGrupy[id] || []).map(function(r: any) { return r.key; });
    }
    // Statusy zastane w danych, których nie ma jeszcze w drzewku, pozostają
    // dostępne jako fallback — nic nie znika po migracji lub imporcie legacy.
    Object.keys(statusCountsByKey).forEach(function(key) {
      if (!key || seenStatusKeys[key]) return;
      seenStatusKeys[key] = true;
      statusRows.push({ label: key, key: key, color: '#6b7280', sort_order: 9999,
                        parent: '', poziom: 0, dzieci: [], grupa: 0 });
      wgKlucza[key] = statusRows[statusRows.length - 1];
    });
    function _porownaj(a: any, b: any): number {
      var pa = _stPriority(a.key), pb = _stPriority(b.key);
      if (pa !== pb) return pa - pb;
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.label.localeCompare(b.label);
    }
    /** Jeden status w pasku. `wciecie` > 0 = status siedzi w grupie. */
    function _rysujStatus(row: any, wciecie: number): void {
      // Nazwa nadana przez firmę bije tłumaczenie; klucz zostaje tożsamością.
      var label = _etykietaStatusu(row.key, row.label);
      var _count = Number(statusCountsByKey[row.key] || 0);
      var isActive = (state as any).ordersStatusFilter === row.key
        && (state as any).ordersSourceFilter === 'all' && (state as any).ordersPaymentFilter === 'all';
      var _stInfo = orderTreeMap[row.key];
      var _dotColor = (_stInfo && _stInfo.color) || row.color || '#6b7280';
      statusBody += '<div class="osp-item' + (isActive ? ' active' : '') + '" role="button" tabindex="0"' +
        ' data-filter-type="status" data-filter-val="' + escapeAttr(row.key) + '"' +
        (wciecie ? ' style="padding-left:' + (10 + wciecie) + 'px"' : '') + '>' +
        '<span style="display:inline-flex;align-items:center;gap:6px">' +
          '<span style="width:8px;height:8px;border-radius:50%;background:' + _kolorBezpieczny(_dotColor) + ';flex-shrink:0"></span>' +
          escapeHtml(label) +
        '</span><span class="osp-badge">' + _count + '</span></div>';
    }

    // GRUPY 2.0 (0234): JEDNA sekcja grup we wspolnej kolejnosci — warunkowe
    // i reczne razem (reczna ma rączkę; klik filtruje jej przypisania). Wewnatrz
    // rozwinietej grupy status pokazuje licznik PRZECIECIA („ktore Nowe jest
    // ktore" — decyzja usera), a klik statusu filtruje dokladnie to przeciecie.
    grupy.slice().sort(function(a, b) {
      return (Number(a.sort_order || 0) - Number(b.sort_order || 0))
          || String(a.name || '').localeCompare(String(b.name || ''));
    }).forEach(function(g) {
      var czyReczna = String((g as any).group_type || 'auto') === 'manual';
      var idG = String(g.id);
      var kolorG = String(g.color || '#6b7280');
      if (czyReczna) {
        var aktywnaM = String((state as any).ordersManualGroup || '') === idG;
        statusBody += '<div class="osp-item osp-grupa' + (aktywnaM ? ' active' : '') + '" role="button" tabindex="0"' +
          ' data-filter-type="manual-group" data-filter-val="' + escapeAttr(idG) + '">' +
          '<span style="display:inline-flex;align-items:center;gap:7px;min-width:0">' +
            '<span style="flex-shrink:0;font-size:12px;line-height:1;color:' + _kolorBezpieczny(kolorG) + '">✋</span>' +
            '<a class="osp-grupa-nazwa" href="' + escapeAttr(_hrefFiltra('manual-group', idG)) + '">' + escapeHtml(String(g.name || '')) + '</a>' +
          '</span>' +
          '<span style="display:inline-flex;align-items:center;gap:4px;flex-shrink:0">' +
            '<span class="osp-badge">' + Number(manualCounts[idG] || 0) + '</span>' +
            _przyciskiWidokuGrupy(g, idG, true) +
          '</span></div>';
        // Odbiór usera 2026-08-19 #2: rozkład statusów zamówień w grupie
        // RĘCZNEJ wykodowany — treścią grupy roboczej są stany PRODUKCJI.
        // PRODUKCJA v2 (2026-08-19): stany PRODUKCJI zawartości grupy —
        // „🔧 Montuje · Paweł 2" — klik filtruje przecięcie grupa∩stan∩osoba.
        if (_widokGrupy(g, 'production')) {
          var prodMapa: any = ((serverStats as any) && (serverStats as any).manual_groups_production) || {};
          var prodWiersze: any[] = (prodMapa[idG] || []).slice()
            .sort(function(a: any, b: any) { return Number(b.count || 0) - Number(a.count || 0); });
          var PROD_ETYKIETY: any = { reported: 'Zgłoszone', queued: 'W kolejce druku',
            printing: 'Drukuje się', printed: 'Wydrukowane', assembling: 'Montuje',
            assembled: 'Zmontował', cancelled: 'Anulowane', failed: 'Druk nieudany' };
          var PROD_KOLORY: any = { reported: '#f59e0b', queued: '#94a3b8', printing: '#3b82f6',
            printed: '#22c55e', assembling: '#f59e0b', assembled: '#22c55e',
            cancelled: '#94a3b8', failed: '#ef4444' };
          if (!prodWiersze.length) {
            statusBody += '<div class="osp-item" style="padding-left:24px;color:var(--color-text-muted);font-size:11px;cursor:default">brak pozycji w produkcji</div>';
          } else {
            prodWiersze.forEach(function(pw: any) {
              var et = PROD_ETYKIETY[String(pw.status)] || String(pw.status);
              var kol = PROD_KOLORY[String(pw.status)] || '#6b7280';
              var opis = et + (pw.worker ? ' · ' + String(pw.worker) : '');
              var aktywnyP = String((state as any).ordersManualGroup || '') === idG &&
                String((state as any).ordersProductionStatus || '') === String(pw.status) &&
                String((state as any).ordersProductionWorker || '') === String(pw.worker || '__bez__');
              statusBody += '<div class="osp-item' + (aktywnyP ? ' active' : '') + '"' +
                ' data-filter-type="manual-group-production"' +
                ' data-filter-val="' + escapeAttr(idG + '|' + String(pw.status) + '|' + encodeURIComponent(String(pw.worker || ''))) + '"' +
                ' style="padding-left:24px">' +
                '<span style="display:inline-flex;align-items:center;gap:6px;min-width:0">' +
                  '<span style="width:8px;height:8px;border-radius:50%;background:' + kol + ';flex-shrink:0"></span>' +
                  '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(opis) + '</span>' +
                '</span><span class="osp-badge">' + Number(pw.count || 0) + '</span></div>';
            });
          }
        }
        statusBody += _kontenerTagowHtml(g, idG, '', '', '');
        return;
      }
      var wGrupie = (statusyGrupy[Number(g.id)] || []).slice().sort(_porownaj);
      // Pusta grupa ZOSTAJE widoczna — user wlasnie ja zalozyl i za chwile
      // uzupelni warunek. Znikanie wygladaloby jak blad zapisu.
      var kluczeG = _kluczeGrupy(Number(g.id));
      var wartoscG = kluczeG.join(',');
      var dimsG = _dimsGrupy(g).join(',');
      var shipsG = _shipsGrupy(g).join(',');
      // 2026-09-03 (audyt): tożsamość grupy = klucze + warunek płatności + warunek wysyłki
      // (dwie grupy o tych samych statusach i innym warunku świeciły obie, F5 wracał do pierwszej).
      var _dimsAkt = String((state as any).ordersGroupPaymentDims || '') === dimsG &&
        String((state as any).ordersGroupShipmentDims || '') === shipsG;
      var aktywnaG = !!wartoscG && (state as any).ordersStatusFilter === wartoscG && _dimsAkt;
      var aktywnyWSrodku = kluczeG.indexOf(String((state as any).ordersStatusFilter || '')) >= 0 && _dimsAkt;
      // 0245: widoczność kategorii per UŻYTKOWNIK (nadpisanie → domyślka grupy).
      var rozwinieta = _widokGrupy(g, 'statuses');
      statusBody += '<div class="osp-item osp-grupa' + (aktywnaG ? ' active' : '') +
        (!rozwinieta && aktywnyWSrodku ? ' osp-grupa--wybor-w-srodku' : '') + '" role="button" tabindex="0"' +
        ' data-filter-type="status" data-filter-val="' + escapeAttr(wartoscG) + '"' +
        (dimsG ? ' data-payment-dims="' + escapeAttr(dimsG) + '"' : '') +
        (shipsG ? ' data-shipment-dims="' + escapeAttr(shipsG) + '"' : '') + '>' +
        '<span style="display:inline-flex;align-items:center;gap:7px;min-width:0">' +
          _ikonaGrupy(kolorG) +
          '<a class="osp-grupa-nazwa" href="' + escapeAttr(_hrefFiltra('status', wartoscG, dimsG, shipsG)) + '">' + escapeHtml(String(g.name || '')) + '</a>' +
        '</span>' +
        '<span style="display:inline-flex;align-items:center;gap:4px;flex-shrink:0">' +
          '<span class="osp-badge">' + _sumaGrupy(Number(g.id), g) + '</span>' +
          _przyciskiWidokuGrupy(g, idG, false) +
        '</span></div>';
      if (rozwinieta) wGrupie.forEach(function(r: any) {
        // Wiersz statusu WEWNATRZ grupy: licznik przeciecia + filtr przeciecia
        // (klik = status + warunek grupy, nie globalny status).
        var przec = maPrzekroj ? _przeciecieStatusu(r.key, g) : Number(statusCountsByKey[r.key] || 0);
        var label = _etykietaStatusu(r.key, r.label);
        var _stInfo = orderTreeMap[r.key];
        var _dotColor = (_stInfo && _stInfo.color) || r.color || '#6b7280';
        // 2026-09-03 (audyt): status wewnątrz rozwiniętej grupy nigdy nie dostawał .active.
        var _aktW = String((state as any).ordersStatusFilter || '') === r.key && _dimsAkt;
        statusBody += '<div class="osp-item' + (_aktW ? ' active' : '') + '" role="button" tabindex="0"' +
          ' data-filter-type="status" data-filter-val="' + escapeAttr(r.key) + '"' +
          (dimsG ? ' data-payment-dims="' + escapeAttr(dimsG) + '"' : '') +
          (shipsG ? ' data-shipment-dims="' + escapeAttr(shipsG) + '"' : '') +
          ' style="padding-left:24px">' +
          '<span style="display:inline-flex;align-items:center;gap:6px">' +
            '<span style="width:8px;height:8px;border-radius:50%;background:' + _kolorBezpieczny(_dotColor) + ';flex-shrink:0"></span>' +
            escapeHtml(label) +
          '</span><span class="osp-badge">' + przec + '</span></div>';
      });
      // 0237: TAGI zamówień tej grupy — stała widoczność (show_tags) przy
      // rozwiniętej grupie albo jednorazowy podgląd ikonką.
      statusBody += _kontenerTagowHtml(g, idG, wartoscG, dimsG, shipsG);
    });
    statusRows.filter(function(r) { return !wJakiejsGrupie[r.key]; }).sort(_porownaj)
      .forEach(function(r) { _rysujStatus(r, 0); });

    // ── ŁĄCZNY LICZNIK „DO WYSŁANIA" (2026-08-27, zgłoszenie usera) ────────
    // „licznik musi liczyć wszystkie paczki, które są do wysłania" — nie tylko
    // grupę W REALIZACJI, bo pierwszeństwo grup roboczych WYJMUJE z niej
    // zamówienia będące w produkcji (DO DRUKU, ZMONTOWAĆ…).
    //
    // Suma = grupy AUTOMATYCZNE z samą pracą do wysłania + WSZYSTKIE grupy
    // robocze. Grupa automatyczna liczy się, gdy:
    //   • żaden z jej statusów nie jest FINALNY (wysłane/odebrane/anulowane/
    //     zwrot — u tej firmy „Zrealizowane" trzyma też `packed`, dlatego
    //     wykluczamy CAŁĄ grupę z finałem, a nie pojedyncze klucze),
    //   • jej warunek płatności nie ogranicza się do NIEOPŁACONYCH
    //     („WSTRZYMANE" = te same statusy co realizacja, ale pending —
    //     to poczekalnia, nie paczki do wysłania).
    // Bez nazw grup — nazwy są zmienialne (ta sama pułapka co przy statusach).
    try {
      var _FINALNE: Record<string, boolean> = { shipped: true, delivered: true, picked_up: true,
        ready_for_pickup: true, cancelled: true, returned: true, refunded: true };
      var _doWyslania = 0;
      grupy.forEach(function(g: any) {
        var idG2 = Number(g.id);
        if (String(g.group_type || 'auto') === 'manual') {
          _doWyslania += Number(manualCounts[String(idG2)] || (manualCounts as any)[idG2] || 0);
          return;
        }
        var statusyG = statusyGrupy[idG2] || [];
        if (!statusyG.length) return;
        if (statusyG.some(function(r: any) { return _FINALNE[String(r.key || '').toLowerCase()]; })) return;
        // Porównanie po CAŁYCH tokenach — 'partially_paid' zawiera 'paid' jako
        // podnapis i substring-test wciągał WSTRZYMANE (pending,partially_paid)
        // do licznika (złapane na symulacji na żywych danych, 2026-08-27).
        var platnosci = String(g.payment_values || '').toLowerCase()
          .split(',').map(function(x: string) { return x.trim(); }).filter(Boolean);
        if (platnosci.length && platnosci.indexOf('paid') === -1 && platnosci.indexOf('cod') === -1) return;
        // 2026-09-03: audyt zgłosił, że grupa auto BEZ warunku sumuje by_key (który nie
        // wyklucza grup roboczych), więc jej zamówienia mogłyby policzyć się drugi raz
        // w manualCounts. ŚWIADOMIE zostawiamy `_sumaGrupy`: chip ma być sumą tego, co
        // widać na pasku (kontrakt test_licznik_do_wyslania), a u tej firmy jedyne grupy
        // bez warunku to „Zrealizowane" i „Anulowane" — finalne, odpadają wyżej, więc
        // dubel nie występuje. Prawdziwa naprawa (gdyby ktoś założył grupę bez warunku ze
        // statusem niefinalnym) to policzenie UNIKALNYCH zamówień po stronie serwera.
        _doWyslania += _sumaGrupy(idG2, g);
      });
      var chip = document.getElementById('orders-toship-chip');
      if (chip) {
        chip.style.cssText = 'display:inline-flex;align-items:center;gap:6px;padding:5px 12px;'
          + 'border-radius:999px;border:1px solid rgba(59,130,246,.4);background:rgba(59,130,246,.08);'
          + 'color:var(--color-primary);font-size:12.5px;font-weight:700;white-space:nowrap;flex-shrink:0';
        chip.innerHTML = '<i data-lucide="package" style="width:14px;height:14px"></i>'
          + 'Do wysłania: ' + _doWyslania;
        try { (window as any).lucide?.createIcons?.({ nodes: [chip] }); } catch (_e) { /* noop */ }
      }
    } catch (_e) { /* licznik jest dodatkiem — nie może wywrócić paska */ }

    // ─── PŁATNOŚĆ ─── (z drzewka SellBase, wszystkie zdefiniowane statusy)
    var paymentBody = '';
    // Unikalne klucze statusów z drzewka (tylko root = parent_key jest null)
    var paymentKeysOrdered: StatusMetaItem[] = [];
    var seenPayKeys: Record<string, boolean> = {};
    if ((window.__sbStateExt && (window.__sbStateExt as any)._paymentStatusTreeMap)) {
      // Sortuj po sort_order jeśli jest
      var payEntries: StatusMetaItem[] = [];
      Object.keys((window.__sbStateExt && (window.__sbStateExt as any)._paymentStatusTreeMap)).forEach(function(k) {
        var s = (window.__sbStateExt && (window.__sbStateExt as any)._paymentStatusTreeMap)[k];
        if (!s || !s.key) return;
        if (s.parent_key) return; // tylko rodzice
        var kk = String(s.key).toLowerCase();
        if (seenPayKeys[kk]) return;
        seenPayKeys[kk] = true;
        payEntries.push(s);
      });
      payEntries.sort(function(a: StatusMetaItem, b: StatusMetaItem) { return (a.sort_order || 0) - (b.sort_order || 0); });
      paymentKeysOrdered = payEntries.map(function(s: StatusMetaItem) { return s; });
    }
    // Jeśli drzewko jeszcze nie załadowane — fallback na klucze z orderów
    if (!paymentKeysOrdered.length) {
      paymentKeysOrdered = Object.keys(paymentStatusCounts).map(function(k) {
        return { key: k, name: (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, ' '), color: '#6b7280' };
      });
    }
    paymentKeysOrdered.forEach(function(s: StatusMetaItem) {
      var key = String(s.key).toLowerCase();
      var count = paymentStatusCounts[key] || 0;
      if (count === 0) return; // ukryj puste
      var isActive = (state as any).ordersPaymentFilter === key;
      var color = s.color || '#6b7280';
      var name = s.name || key;
      paymentBody += '<div class="osp-item' + (isActive ? ' active' : '') + '" data-filter-type="payment" data-filter-val="' + escapeAttr(key) + '">' +
        '<span style="display:inline-flex;align-items:center;gap:6px"><span style="width:8px;height:8px;border-radius:50%;background:' + _kolorBezpieczny(color) + ';flex-shrink:0"></span>' + escapeHtml(name) + '</span><span class="osp-badge">' + count + '</span></div>';
    });
    // "Pobranie" (COD) — pochodna metody płatności, nie statusu SellBase; pokaż jeśli > 0
    if (codCount > 0) {
      var isCodActive = (state as any).ordersPaymentFilter === 'cod';
      paymentBody += '<div class="osp-item' + (isCodActive ? ' active' : '') + '" data-filter-type="payment" data-filter-val="cod">' +
        '<span style="display:inline-flex;align-items:center;gap:6px"><span style="width:8px;height:8px;border-radius:50%;background:#0ea5e9;flex-shrink:0"></span>Pobranie</span><span class="osp-badge">' + codCount + '</span></div>';
    }

    // ─── PRZESYŁKA ───
    var shipBody = '';
    var shipLabels = Object.keys(shipmentCounts).sort();
    shipLabels.forEach(function(label) {
      var _shipInfo = (window.__sbStateExt && (window.__sbStateExt as any)._shipmentStatusTreeMap) ? (window.__sbStateExt && (window.__sbStateExt as any)._shipmentStatusTreeMap)[label] : null;
      var _dotColor = _shipInfo ? _shipInfo.color : '#9ca3af';
      var _displayName = _shipInfo ? _shipInfo.name : label;
      var isShipActive = ((state as any).ordersShipmentFilter || 'all') === label;
      shipBody += '<div class="osp-item' + (isShipActive ? ' active' : '') + '" data-filter-type="shipment" data-filter-val="' + escapeAttr(label) + '">' +
        '<span style="display:inline-flex;align-items:center;gap:6px"><span style="width:8px;height:8px;border-radius:50%;background:' + _kolorBezpieczny(_dotColor) + ';flex-shrink:0"></span>' + escapeHtml(_displayName) + '</span><span class="osp-badge">' + shipmentCounts[label] + '</span></div>';
    });

    // ─── ŹRÓDŁO ───
    var SOURCE_META: Record<string, any> = {
      allegro:     { label: 'Allegro',     color: '#f97316' },
      prestashop:  { label: 'PrestaShop',  color: '#8b5cf6' },
      erli:        { label: 'Erli',        color: '#ec4899' },
      ebay:        { label: 'eBay',        color: '#3665f3' },
      tiktok:      { label: 'TikTok Shop', color: '#FE2C55' },
      vonhalsky:   { label: 'Von Halsky',  color: '#FFCB39' },
      manual:      { label: 'Ręczne',      color: '#22c55e' },
      woocommerce: { label: 'WooCommerce', color: '#7f54b3' },
    };
    var connectedKeys = ((state as any).connectedIntegrations && state.connectedIntegrations.keys) || [];
    connectedKeys.forEach(function(k: string) { if (!(k in sourceCounts)) sourceCounts[k] = 0; });
    if (!('manual' in sourceCounts)) sourceCounts['manual'] = 0;
    var orderedSrc = Object.keys(sourceCounts).sort(function(a, b) {
      if (a === 'manual') return 1;
      if (b === 'manual') return -1;
      return a.localeCompare(b);
    });
    // Sub-items: lista kont Allegro pod 'Allegro' (rozwijana chevronem)
    var allegroAccounts = ((state as any).connectedIntegrations && (state as any).connectedIntegrations.accounts && (state as any).connectedIntegrations.accounts.allegro) || (state as any).allegroAccounts || [];
    var byAccount: any = (serverStats && serverStats.by_account && typeof serverStats.by_account === 'object') ? serverStats.by_account : null;

    // Lazy fetch jeśli connectedIntegrations jeszcze nie pobrane.
    // 2026-06-02 FIX (pętla nieskończona / freeze dla tenanta z 0 kontami Allegro):
    // poprzednio guard sprawdzał TYLKO `_allegroAccountsFetching`. Dla firmy bez kont
    // Allegro fetch zwracał [] → `allegroAccounts` zostawało puste → `_allegroAccountsFetching`
    // wracało na false → re-render → warunek `!allegroAccounts.length && !fetching` znów
    // PRAWDA → kolejny fetch → re-render → STORM (_renderOrdersStatusPanel w kółko, ~∞)
    // → zamrożenie wątku, „Ładowanie zamówień…" na zawsze. Konta Z kontami nie łapały
    // (lista niepusta → warunek false). FIX: flaga `_allegroAccountsFetched` — fetch raz,
    // pusty wynik NIE re-triggeruje (próbujemy tylko gdy jeszcze nie próbowaliśmy).
    if ((!allegroAccounts || !allegroAccounts.length)
        && !((state as any)._allegroAccountsFetching)
        && !((state as any)._allegroAccountsFetched)) {
      (state as any)._allegroAccountsFetching = true;
      try {
        (window as any).SellBaseAPI && (window as any).SellBaseAPI.getAccounts &&
          (window as any).SellBaseAPI.getAccounts().then(function(r: { accounts?: IntegrationAccount[] } | IntegrationAccount[]) {
            var accs = ((r && ((r as any).accounts || r)) || []) as IntegrationAccount[];
            (state as any).allegroAccounts = accs.filter(function(a: IntegrationAccount) { return a && a.is_active !== 0 && a.is_active !== false; });
            (state as any)._allegroAccountsFetching = false;
            (state as any)._allegroAccountsFetched = true;
            // Re-render TYLKO gdy fetch faktycznie coś przyniósł — inaczej (pusty wynik)
            // nic się nie zmienia, a niepotrzebny re-render to czysty narzut.
            if ((state as any).currentView === 'orders' && (state as any).allegroAccounts.length) _renderOrdersStatusPanel();
          }).catch(function() { (state as any)._allegroAccountsFetching = false; (state as any)._allegroAccountsFetched = true; });
      } catch (e: any) { (state as any)._allegroAccountsFetching = false; (state as any)._allegroAccountsFetched = true; }
    }

    var sourceBody = '';
    orderedSrc.forEach(function(key: string) { var meta = SOURCE_META[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), color: '#6b7280' };
      var count = sourceCounts[key] || 0;
      var isConnected = connectedKeys.indexOf(key) !== -1 || key === 'manual';
      if (count === 0 && !isConnected) return;
      var isActive = (state as any).ordersSourceFilter === key && !(state as any).ordersAccountFilter;
      // 2026-05-20: Allegro + sub-lista kont — ZAWSZE widoczna (bez chevron toggle).
      // Per user: klik na 'Allegro' = filter wszystkie konta, klik na konto = filter per konto.
      if (key === 'allegro' && allegroAccounts && allegroAccounts.length > 0) {
        sourceBody += '<div class="osp-item' + (isActive ? ' active' : '') + '" data-filter-type="source" data-filter-val="' + escapeAttr(key) + '">' +
          '<span style="display:inline-flex;align-items:center;gap:6px">' +
            '<span style="width:8px;height:8px;border-radius:50%;background:' + meta.color + ';flex-shrink:0"></span>' +
            escapeHtml(meta.label) +
          '</span>' +
          '<span class="osp-badge">' + count + '</span>' +
        '</div>';
        // Sub-items zawsze pod 'Allegro' (flat list, indent + mniejsza czcionka)
        allegroAccounts.forEach(function(acc: any) {
          if (!acc) return;
          var accId = acc.id || acc.account_id;
          if (!accId) return;
          var accName = acc.name || acc.login || acc.allegro_login || acc.allegro_user_name || ('#' + accId);
          // by_account keyowane "marketplace:id" (id kont różnych źródeł kolidują);
          // fallback na goły id = kompatybilność ze starym backendem w okresie przejściowym.
          var _accKey = 'allegro:' + String(accId);
          var accCount = (byAccount && (byAccount[_accKey] != null)) ? byAccount[_accKey]
            : (byAccount && (byAccount[String(accId)] != null)) ? byAccount[String(accId)] : '—';
          var accActive = String((state as any).ordersAccountFilter || '') === String(accId);
          var envSandbox = acc.is_sandbox === 1 || acc.is_sandbox === true || acc.environment === 'sandbox';
          var envBadge = envSandbox
            ? '<span style="font-size:9px;font-weight:700;letter-spacing:0.5px;padding:1px 5px;border-radius:3px;background:#fff3cd;color:#856404;border:1px solid #ffe69c;margin-left:6px">SANDBOX</span>'
            : '';
          sourceBody += '<div class="osp-item osp-sub-item' + (accActive ? ' active' : '') + '" data-filter-type="account" data-filter-val="' + escapeAttr(String(accId)) + '" style="padding-left:28px;font-size:12px">' +
            '<span style="display:inline-flex;align-items:center;min-width:0;overflow:hidden">' +
              '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(String(accName)) + '</span>' +
              envBadge +
            '</span>' +
            '<span class="osp-badge">' + escapeHtml(String(accCount)) + '</span>' +
          '</div>';
        });
      } else {
        sourceBody += '<div class="osp-item' + (isActive ? ' active' : '') + '" data-filter-type="source" data-filter-val="' + escapeAttr(key) + '">' +
          '<span style="display:inline-flex;align-items:center;gap:6px">' +
            '<span style="width:8px;height:8px;border-radius:50%;background:' + meta.color + ';flex-shrink:0"></span>' +
            escapeHtml(meta.label) +
          '</span>' +
          '<span class="osp-badge">' + count + '</span>' +
        '</div>';
      }
    });

    // 2026-08-17 decyzja usera: "Wszystkie" schodzi NA SAM DÓŁ, pod sekcję „Tagi
    // zamówień" — domyślnym widokiem po wejściu na /orders jest PIERWSZA GRUPA paska
    // (auto-wybór niżej), więc "Wszystkie" pełni już tylko rolę resetu filtrów.
    // Kolejność: Statusy → Płatność → Przesyłka → Źródło (Allegro + sub-konta)
    // → Tagi zamówień → Wszystkie.
    // Grupa systemowa CZARNA LISTA — na samym dole, pod wszystkimi grupami (decyzja usera 2026-09-03).
    statusBody += _wierszCzarnejListy(Number(serverStats && serverStats.blacklist_count || 0));
    var html = '';
    html += _ospSectionHtml('statuses', 'Statusy', statusBody);
    // GRUPY 2.0: osobna sekcja „Grupy robocze" USUNIETA — reczne stoja w JEDNEJ
    // liscie GRUPY wyzej (z rączką), we wspolnej kolejnosci (decyzja usera).
    if (paymentBody) html += _ospSectionHtml('payment', 'Płatność', paymentBody);
    if (shipBody)    html += _ospSectionHtml('shipment', 'Przesyłka', shipBody);
    if (sourceBody)  html += _ospSectionHtml('source', 'Źródło', sourceBody);
    // ETAP 4 (2026-08-10): TAGI ZAMOWIEN. Sekcja na DOLE, pod zrodlem — tagi sa
    // narzedziem porzadkowania, a nie glownym filtrem, wiec nie odpychaja statusow.
    var _tagi = (window as any).__sbOrderTags;
    // Dociagniecie przy PIERWSZYM rysowaniu — panel rysuje sie synchronicznie, a lista
    // tagow przychodzi z sieci. Bez tego sekcja twierdzila „Brak tagow" tylko dlatego,
    // ze jeszcze nikt nie zapytal (zgloszenie usera: „czasem nie widac").
    if (_tagi && typeof _tagi.zapewnijTagi === 'function') _tagi.zapewnijTagi();
    if (_tagi) html += _ospSectionHtml('order-tags', 'Tagi zamówień', _tagi.ciloPanelTagow());
    html += allItemHtml;

    // S-139: ten sam HTML panelu (liczniki bez zmian — najczęstszy przypadek po zapisie i co 30 s) = bez przebudowy:
    // hover, fokus, scroll panelu i ewentualne liczby dopisane przez statystyki serwera zostają
    if ((panel as any).__sbOstatniHtml === html && panel.childElementCount) {
      syncOrdersSearchScopeUI();
      _autoWybierzPierwszaGrupe(panel);
      // S-139 przegląd: ścieżka pominięcia też dociąga liczniki tag×grupa (własny cache 60 s) —
      // bez tego są stare po nadaniu/zdjęciu tagu, gdy reszta HTML panelu wyszła identyczna.
      _zaladujTagiGrup(panel, { tylkoNieswieze: true });
      return;
    }
    (panel as any).__sbOstatniHtml = html;
    var _przywrocPanel = zapamietajWidok(panel);
    panel.innerHTML = html;

    // PULAPKA: wszystkie pozycje panelu dziela JEDEN listener nizej, ktory czyta
    // `data-filter-type`. Wiersz tagu go NIE MA — wiec bez wlasnego podpiecia
    // cicho wykonalby galaz „status" i wyczyscilby filtry.
    if (_tagi) { try { _tagi.podepnijPanelTagow(panel); } catch (_e) { /* panel dziala dalej */ } }

    // Bind collapse toggles
    panel.querySelectorAll('.osp-section-title.osp-collapsible').forEach(function(h) {
      h.addEventListener('click', function(this: any) {
        var key = this.dataset.ospSection;
        var body = panel!.querySelector('.osp-section-body[data-osp-body="' + key + '"]');
        var closed = this.classList.toggle('osp-closed');
        var chev = this.querySelector('.osp-chevron');
        if (chev) chev.style.transform = closed ? 'rotate(-90deg)' : 'rotate(0deg)';
        if (body) {
          body.classList.toggle('osp-closed', closed);
          (body as any).style.display = closed ? 'none' : '';
        }
        _ospSetCollapsed(key, closed);
      });
    });

    // Bind sub-chevron toggle (Allegro accounts sub-list) — przed bind .osp-item żeby stopPropagation działało
    panel.querySelectorAll('[data-osp-sub-toggle]').forEach(function(el) {
      el.addEventListener('click', function(this: any, ev: any) {
        ev.stopPropagation();
        var key = this.getAttribute('data-osp-sub-toggle');
        var closed = !_ospIsCollapsed(key);
        _ospSetCollapsed(key, closed);
        _renderOrdersStatusPanel();
      });
    });

    // 0237: leniwe dociągnięcie liczników tag×grupa dla widocznych kontenerów.
    _zaladujTagiGrup(panel);
    // 0239: dwa przełączniki widoku grupy (⊞ kategorie / 🏷 tagi) — siedzą
    // WEWNĄTRZ wiersza grupy, więc stopPropagation przed filtrem. Zmiana jest
    // OPTYMISTYCZNA (flaga w danych grupy + przerysowanie panelu od razu),
    // zapis PUT w tle; błąd cofa flagę z toastem. Flaga żyje NA GRUPIE w bazie
    // = wspólna dla całej firmy.
    panel.querySelectorAll('[data-osp-widok]').forEach(function(el) {
      var _tgl = function (this: any, ev: any) {
        ev.stopPropagation(); ev.preventDefault();
        var idG = String(this.getAttribute('data-osp-widok-grupa') || '');
        var rodzaj = String(this.getAttribute('data-osp-widok') || '');
        if (!idG || !rodzaj) return;
        var grupy: any[] = (window.__sbStateExt && (window.__sbStateExt as any)._statusGroups) || [];
        var g = grupy.filter(function (x: any) { return String(x.id) === idG; })[0];
        if (!g) return;
        // 0245: klik NIE dotyka już grupy w bazie (to nadpisywało widok całej
        // firmie) — zapisuje OSOBISTE nadpisanie w user_ui_prefs ('widok_grup').
        var klucz = (rodzaj === 'statuses' || rodzaj === 'tags' || rodzaj === 'production') ? rodzaj : 'tags';
        var bylo = _widokGrupy(g, klucz as any);
        var ext: any = (window as any).__sbStateExt = (window as any).__sbStateExt || {};
        var mapa: any = ext._widokGrupUser = ext._widokGrupUser || {};
        var wpis: any = mapa[idG] = mapa[idG] || {};
        var poprzednie = wpis[klucz];
        wpis[klucz] = !bylo;
        _renderOrdersStatusPanel();
        try {
          (window as any).SellBaseAPI.request('PUT', '/api/user-prefs/widok_grup', { value: mapa })
            .catch(function (e: any) {
              if (poprzednie === undefined) delete wpis[klucz]; else wpis[klucz] = poprzednie;
              _renderOrdersStatusPanel();
              if (typeof (window as any).showToast === 'function') {
                (window as any).showToast('Nie udało się zapisać widoku: ' + ((e && e.message) || ''), 'error');
              }
            });
        } catch (_e) { /* brak API — sam widok lokalny */ }
      };
      el.addEventListener('click', _tgl);
      el.addEventListener('keydown', function(this: any, ev: any) {
        if (ev.key === 'Enter' || ev.key === ' ') _tgl.call(this, ev);
      });
    });

    // Bind filter clicks
    panel.querySelectorAll('.osp-item').forEach(function(item) {
      // 2026-09-08 (prośba właściciela): środkowy przycisk na wierszu (poza linkiem, który obsługuje
      // przeglądarka) = lista z tym filtrem w nowej karcie.
      item.addEventListener('auxclick', function(this: any, ev: any) {
        if (!ev || ev.button !== 1 || !this.dataset.filterType) return;
        if (ev.target && ev.target.closest && ev.target.closest('a')) return;
        ev.preventDefault();
        try {
          window.open(_hrefFiltra(String(this.dataset.filterType), String(this.dataset.filterVal || ''),
            String(this.dataset.paymentDims || ''), String(this.dataset.shipmentDims || '')), '_blank', 'noopener');
        } catch (_ea) { /* nowa karta to udogodnienie */ }
      });
      item.addEventListener('click', function(this: any, ev: any) {
        // 2026-09-08: nazwa grupy to <a href>. Ctrl/⌘/Shift+klik w link zostawiamy przeglądarce (nowa
        // karta), poza linkiem otwieramy nową kartę sami; zwykły klik w link = filtr bez przeładowania.
        var _wLink = !!(ev && ev.target && ev.target.closest && ev.target.closest('a.osp-grupa-nazwa'));
        var _mod = !!(ev && (ev.ctrlKey || ev.metaKey || ev.shiftKey));
        if (_mod) {
          if (!_wLink && this.dataset.filterType) {
            try {
              window.open(_hrefFiltra(String(this.dataset.filterType), String(this.dataset.filterVal || ''),
                String(this.dataset.paymentDims || ''), String(this.dataset.shipmentDims || '')), '_blank', 'noopener');
            } catch (_eo) { /* nowa karta to udogodnienie */ }
          }
          return;
        }
        if (_wLink && ev && ev.preventDefault) ev.preventDefault();
        // REVIEW 2026-08-17 (F2): RĘCZNY klik gasi auto-wybór pierwszej grupy —
        // bez tego spóźnione dojście listy grup (albo wygaszacz) nadpisywało
        // świeżo wybrany przez usera filtr pierwszą grupą.
        (window as any).__sbOrdersAutoPierwszaGrupa = false;
        (window as any).__sbOrdersAutoGrupaFetchPominiety = false;
        if (_autoGrupaTimer !== null) { clearTimeout(_autoGrupaTimer); _autoGrupaTimer = null; }
        var ftype = this.dataset.filterType;
        var fval = this.dataset.filterVal;
        // 2026-09-03 (audyt): wiersz informacyjny („brak pozycji w produkcji") też ma klasę
        // osp-item — bez filtra klik kasował zapamiętaną grupę i pokazywał wszystko.
        if (!ftype) return;
        // 2026-09-03: 'blacklist' = grupa systemowa CZARNA LISTA — ten sam tor co grupy
        // (bez tego klik spadał do gałęzi Źródło/Płatność, która nie zna tego filtra).
        if (ftype === 'manual-group' || ftype === 'status' || ftype === 'manual-group-status' || ftype === 'manual-group-production' || ftype === 'blacklist') {
          // Wiersz grupy / statusu / „Wszystkie" — WSPÓLNY tor z auto-wyborem
          // pierwszej grupy (zastosujFiltrGrupy): mutacja stanu + fetch w jednym miejscu.
          zastosujFiltrGrupy(String(ftype), String(fval || ''),
            String(this.dataset.paymentDims || ''), String(this.dataset.shipmentDims || ''));
          return;
        }
        // 0232: kazdy klik w panelu resetuje filtry kubelkowe (galezie nizej ich
        // nie ustawiaja; galaz statusu robi to w zastosujFiltrGrupy).
        // 2026-08-27: wybór spoza toru grup unieważnia zapamiętaną grupę —
        // inaczej F5 wskrzeszałby filtr, z którego user właśnie wyszedł.
        try { sessionStorage.removeItem('sb_orders_filtr_paska'); } catch (_ePer) {}
        // 2026-09-03 (audyt): Źródło/Konto/Płatność/Przesyłka też zdejmują filtr czarnej listy
        // (wcześniej blacklist=1 leciał dalej, a wiersz CZARNA LISTA świecił obok Allegro).
        try {
          var _otG = (window as any).__sbOrderTags;
          if (_otG && _otG.ustawFiltrCzarnejListy) _otG.ustawFiltrCzarnejListy(false);
        } catch (_eG) { /* filtr systemowy jest dodatkiem */ }
        (state as any).ordersGroupPaymentDims = '';
        (state as any).ordersGroupShipmentDims = '';
        (state as any).ordersGroupBezRob = false;
        (state as any).ordersManualGroup = null;
        // REVIEW v2: bez zera filtr produkcji zostawał „widmem" — klik w
        // Źródło/Konto/Płatność/Przesyłkę dalej zawężał listę do produkcji.
        (state as any).ordersProductionStatus = '';
        (state as any).ordersProductionWorker = '';
        if (ftype === 'source') {
          // 2026-09-03 (audyt): przy wybranym koncie klik w źródło = „wszystkie konta", nie „Wszystkie".
          (state as any).ordersSourceFilter = (fval === state.ordersSourceFilter && !(state as any).ordersAccountFilter) ? 'all' : fval;
          (state as any).ordersStatusFilter = 'all';
          (state as any).ordersPaymentFilter = 'all';
          (state as any).ordersShipmentFilter = 'all';
          (state as any).ordersAccountFilter = null;
        } else if (ftype === 'account') {
          var accId = parseInt(fval, 10) || null;
          (state as any).ordersAccountFilter = accId;
          (state as any).ordersSourceFilter = 'allegro';
          (state as any).ordersStatusFilter = 'all';
          (state as any).ordersPaymentFilter = 'all';
          (state as any).ordersShipmentFilter = 'all';
          (state as any).currentPage.orders = 1;
          if (typeof loadAllegroOrdersIntoPanel === 'function') {
            loadAllegroOrdersIntoPanel(true);
          } else {
            renderOrdersView();
          }
          return;
        } else if (ftype === 'payment') {
          (state as any).ordersPaymentFilter = fval === state.ordersPaymentFilter ? 'all' : fval;
          (state as any).ordersStatusFilter = 'all';
          (state as any).ordersSourceFilter = 'all';
          (state as any).ordersShipmentFilter = 'all';
          (state as any).ordersAccountFilter = null;
        } else if (ftype === 'shipment') {
          (state as any).ordersShipmentFilter = fval === state.ordersShipmentFilter ? 'all' : fval;
          (state as any).ordersStatusFilter = 'all';
          (state as any).ordersSourceFilter = 'all';
          (state as any).ordersPaymentFilter = 'all';
          (state as any).ordersAccountFilter = null;
        }
        (state as any).currentPage.orders = 1;
        // Sprint 25 Faza 5 (2026-05-03): zmiana filter → server fetch (nie tylko slice).
        // Backend zwraca prawidłową stronę z filtrem applied; frontend nie filtruje lokalnie.
        if (typeof loadAllegroOrdersIntoPanel === 'function') {
          loadAllegroOrdersIntoPanel(true);
        } else {
          renderOrdersView();
        }
      });
    });

    // 2026-08-17: jednorazowa konsumpcja flagi routera — wiersze grup są już
    // narysowane i podpięte, więc auto-wybór widzi dokładnie ten DOM co klik.
    syncOrdersSearchScopeUI();
    // 2026-09-03 (audyt): Enter / Spacja na wierszu lub chipie = klik (wiersze mają role=button).
    if (!(panel as any)._sbKlawiatura) {
      (panel as any)._sbKlawiatura = true;
      panel.addEventListener('keydown', function (ev: any) {
        if (ev.key !== 'Enter' && ev.key !== ' ') return;
        var cel = ev.target as HTMLElement;
        if (!cel || !cel.closest) return;
        var el = cel.closest('.osp-item, .osp-tag-chip, [role="button"]') as HTMLElement | null;
        if (!el || el.matches('[data-osp-widok]')) return;
        ev.preventDefault();
        el.click();
      });
    }
    _przywrocPanel();
    _autoWybierzPierwszaGrupe(panel);
}

// ── EXPOSE ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
(window as any)._lazyRefreshVisiblePSOrders = _lazyRefreshVisiblePSOrders; (window as any).SellBaseApp._lazyRefreshVisiblePSOrders = _lazyRefreshVisiblePSOrders;
(window as any)._refreshConnectedIntegrations = _refreshConnectedIntegrations; (window as any).SellBaseApp._refreshConnectedIntegrations = _refreshConnectedIntegrations;
(window as any)._ospIsCollapsed = _ospIsCollapsed; (window as any).SellBaseApp._ospIsCollapsed = _ospIsCollapsed;
(window as any)._ospSectionHtml = _ospSectionHtml; (window as any).SellBaseApp._ospSectionHtml = _ospSectionHtml;
(window as any)._ospSetCollapsed = _ospSetCollapsed; (window as any).SellBaseApp._ospSetCollapsed = _ospSetCollapsed;
(window as any)._renderOrdersStatusPanel = _renderOrdersStatusPanel; (window as any).SellBaseApp._renderOrdersStatusPanel = _renderOrdersStatusPanel;
(window as any).__sbExports = (window as any).__sbExports || {};
(window as any).__sbExports.resetujFiltryPaska = resetujFiltryPaska;
(window as any).__sbExports.wyczyscTagiGrupCache = wyczyscTagiGrupCache;
(window as any).__sbExports.zapiszTagiDoFiltraPaska = zapiszTagiDoFiltraPaska;
